import React, { useState, useEffect, useContext } from "react";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon, PencilIcon, PlusIcon, CalendarIcon, CheckIcon, XCircleIcon, TrashIcon, DocumentChartBarIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { useApiClient } from "../utils/apiClient";
import { AuthContext } from "../contexts/AuthContext";
import { ROLE_CONFIG } from "../constants/roleConfig";
import { USER_COLORS, COLOR_CLASSES } from "../constants/colors";
import { v4 as uuidv4 } from "uuid";

const extractDegreeFromName = (name) => {
    if (!name) return { nameWithoutDegree: name, degree: null };
    const nameParts = name.trim().split(' ');
    if (nameParts.length < 2) return { nameWithoutDegree: name, degree: null };
    
    const lastWord = nameParts[nameParts.length - 1].toUpperCase();
    const commonDegrees = ['MD', 'CRNA', 'RN', 'NP', 'PA', 'DO', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA'];
    
    if (commonDegrees.includes(lastWord)) {
        const nameWithoutDegree = nameParts.slice(0, -1).join(' ');
        return { nameWithoutDegree, degree: lastWord };
    }
    
    return { nameWithoutDegree: name, degree: null };
};

const groupProvidersByDegree = (providers) => {
    const grouped = {};
    
    providers.forEach(provider => {
        const { degree } = extractDegreeFromName(provider.user_name || provider.email || provider.user_id);
        const degreeKey = degree || 'Other';
        
        if (!grouped[degreeKey]) {
            grouped[degreeKey] = [];
        }
        grouped[degreeKey].push(provider);
    });
    
    Object.keys(grouped).forEach(degree => {
        grouped[degree].sort((a, b) => {
            const aName = (a.user_name || a.email || a.user_id || '').toLowerCase();
            const bName = (b.user_name || b.email || b.user_id || '').toLowerCase();
            return aName.localeCompare(bName);
        });
    });
    
    return grouped;
};

const getCalendarStateKey = () => {
    return 'calendar_state';
};

const saveCalendarState = (state) => {
    try {
        const stateToSave = {
            selectedUsers: Array.from(state.selectedUsers),
            selectedCenters: Array.from(state.selectedCenters),
            isDailyView: state.isDailyView,
            isMonthlyView: state.isMonthlyView,
            dailyViewDate: state.dailyViewDate?.toISOString(),
            monthlyViewDate: state.monthlyViewDate?.toISOString(),
            assignmentTypeFilter: state.assignmentTypeFilter,
            selectedDate: state.selectedDate?.toISOString()
        };
        localStorage.setItem(getCalendarStateKey(), JSON.stringify(stateToSave));
    } catch (error) {
        console.error('Error saving calendar state:', error);
    }
};

const loadCalendarState = () => {
    try {
        const saved = localStorage.getItem(getCalendarStateKey());
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                selectedUsers: new Set(parsed.selectedUsers || []),
                selectedCenters: new Set(parsed.selectedCenters || []),
                isDailyView: parsed.isDailyView || false,
                isMonthlyView: parsed.isMonthlyView || false,
                dailyViewDate: parsed.dailyViewDate ? new Date(parsed.dailyViewDate) : new Date(),
                monthlyViewDate: parsed.monthlyViewDate ? new Date(parsed.monthlyViewDate) : new Date(),
                assignmentTypeFilter: parsed.assignmentTypeFilter || 'all',
                selectedDate: parsed.selectedDate ? new Date(parsed.selectedDate) : getSunday(new Date())
            };
        }
    } catch (error) {
        console.error('Error loading calendar state:', error);
    }
    return null;
};

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getSunday(d) {
    d = new Date(d);
    var day = d.getDay(),
        diff = d.getDate() - day;
    return new Date(d.setDate(diff));
}

function getMonday(d) {
    d = new Date(d);
    var day = d.getDay(),
        diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
}

function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function formatDate(date) {
    return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`;
}

function formatDateFull(date) {
    const month = date.toLocaleString("default", { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
}

function toISODate(date) {
    return date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0');
}

function getWeekEndDate(weekStartDate) {
    const startDate = new Date(weekStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    return toISODate(endDate);
}

function format12h(hour, min) {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour < 12 ? "AM" : "PM";
    return `${h}:${min} ${ampm}`;
}

function formatTimeStr(timeStr) {
    if (!timeStr) return '';
    if (typeof timeStr === 'number') {
        const h = Math.floor(timeStr);
        const m = Math.round((timeStr - h) * 60);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
    }
    if (typeof timeStr === 'string' && timeStr.includes(':')) {
        const [hStr, mStr] = timeStr.split(":");
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
    }
    return '';
}

function timeToDecimal(timeStr) {
    if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) {
        return 0;
    }
    const [h, m] = timeStr.split(":").map(Number);
    return h + m / 60;
}

// Generate a unique key for a shift entry to maintain stable order
function getShiftKey(shift) {
    if (shift.shift_id) {
        return shift.shift_id;
    }
    // Use composite key: date + shift_start + shift_end
    return `${shift.date || ''}_${shift.shift_start || ''}_${shift.shift_end || ''}`;
}

// Ensure all schedule entries have unique IDs for stable ordering
function ensureShiftIds(schedule) {
    if (!Array.isArray(schedule)) return schedule;
    return schedule.map((shift, index) => {
        if (!shift.shift_id) {
            // Generate a stable ID based on date, time, and index
            shift.shift_id = shift.date && shift.shift_start && shift.shift_end
                ? `${shift.date}_${shift.shift_start}_${shift.shift_end}_${index}`
                : `shift_${index}_${Date.now()}`;
        }
        return shift;
    });
}

// Find shift by unique identifier (date + shift_start + shift_end)
function findShiftByIdentifier(schedule, targetShift) {
    if (!Array.isArray(schedule) || !targetShift) return -1;
    
    const targetKey = getShiftKey(targetShift);
    
    return schedule.findIndex(shift => {
        const shiftKey = getShiftKey(shift);
        return shiftKey === targetKey;
    });
}

function localDateString(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function getCalendarDays(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    const days = [];
    const currentDay = new Date(startDate);

    while (currentDay <= lastDay || currentDay.getDay() !== 6) {
        days.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
    }
    if (currentDay.getDay() === 6 && currentDay > lastDay) {
        days.push(new Date(currentDay));
    }

    return days;
}

function getAssignmentsForDate(date, assignments) {
    const dateStr = date.toLocaleDateString('en-CA');
    return assignments.filter(appt => appt.date === dateStr);
}

function isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
}

function isCurrentMonth(date, currentDate) {
    return date.getMonth() === currentDate.getMonth();
}

function getShiftType(startTime, endTime) {
    if (!startTime || !endTime) return 'SHIFT';

    try {
        let startHour, startMinute, endHour, endMinute;

        if (typeof startTime === 'string' && startTime.includes(':')) {
            [startHour, startMinute] = startTime.split(':').map(Number);
        } else if (typeof startTime === 'number') {
            startHour = Math.floor(startTime);
            startMinute = (startTime % 1) * 60;
        } else {
            return 'SHIFT';
        }

        if (typeof endTime === 'string' && endTime.includes(':')) {
            [endHour, endMinute] = endTime.split(':').map(Number);
        } else if (typeof endTime === 'number') {
            endHour = Math.floor(endTime);
            endMinute = (endTime % 1) * 60;
        } else {
            return 'SHIFT';
        }

        const startMinutes = startHour * 60 + startMinute;
        let endMinutes = endHour * 60 + endMinute;


        if (endMinutes < startMinutes) {
            endMinutes += 24 * 60;
        }

        if (startMinutes >= 420 && endMinutes <= 900) {
            return 'DAY SHIFT';
        }

        if (startMinutes >= 900 && endMinutes <= 1380) {
            return 'EVENING SHIFT';
        }

        if (startMinutes >= 1380 && endMinutes <= 420) {
            return 'NIGHT SHIFT';
        }
        
        if (startMinutes >= 0 && endMinutes <= 420) {
            return 'NIGHT SHIFT';
        }
        
        if (startMinutes >= 1380 && endMinutes <= 1380 + 24 * 60) {
            return 'NIGHT SHIFT';
        }

        return 'SHIFT';
    } catch (e) {
        return 'SHIFT';
    }
}

function highlightText(text, searchTerm) {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
}

function getFilteredCenters(centers, searchTerm) {
    if (!searchTerm) return centers;
    const lowerSearch = searchTerm.toLowerCase();
    return centers.filter(center => {
        const name = (center.name || '').toLowerCase();
        const id = (center.center_id || '').toLowerCase();
        return name.includes(lowerSearch) || id.includes(lowerSearch);
    });
}

function getFilteredProviders(users, searchTerm) {
    if (!searchTerm) return users;
    const lowerSearch = searchTerm.toLowerCase();
    return users.filter(provider => {
        const name = (provider.user_name || '').toLowerCase();
        const email = (provider.email || '').toLowerCase();
        const id = (provider.user_id || '').toLowerCase();
        return name.includes(lowerSearch) || email.includes(lowerSearch) || id.includes(lowerSearch);
    });
}

function getCertifiedProvidersForCenter(users, centerId, centers) {
    if (!centerId || !centers.length) return [];
    
    const center = centers.find(c => c.center_id === centerId);
    if (!center || !center.certified_providers) return [];
    
    const certifiedIds = center.certified_providers;
    return users.filter(user => certifiedIds.includes(user.user_id));
}

function sortProvidersWithCertifiedFirst(providers, certifiedProviders) {
    const certifiedIds = new Set(certifiedProviders.map(p => p.user_id));
    
    return [...providers].sort((a, b) => {
        const aIsCertified = certifiedIds.has(a.user_id);
        const bIsCertified = certifiedIds.has(b.user_id);
        
        if (aIsCertified && !bIsCertified) return -1;
        if (!aIsCertified && bIsCertified) return 1;
        
        const aName = (a.user_name || a.email || a.user_id || '').toLowerCase();
        const bName = (b.user_name || b.email || b.user_id || '').toLowerCase();
        return aName.localeCompare(bName);
    });
}

function goToPreviousMonth(setMonthlyViewDate) {
    setMonthlyViewDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() - 1);
        return newDate;
    });
}

function goToNextMonth(setMonthlyViewDate) {
    setMonthlyViewDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + 1);
        return newDate;
    });
}

function goToToday(setMonthlyViewDate) {
    setMonthlyViewDate(new Date());
}

function goToCurrentWeek(setSelectedDate) {
    setSelectedDate(getSunday(new Date()));
}

function goToCurrentDay(setDailyViewDate) {
    setDailyViewDate(new Date());
}

function goToDate(selectedDate, isDailyView, isMonthlyView, setDailyViewDate, setMonthlyViewDate, setSelectedDate) {
    if (isDailyView) {
        setDailyViewDate(selectedDate);
    } else if (isMonthlyView) {
        setMonthlyViewDate(selectedDate);
    } else {
        setSelectedDate(getSunday(selectedDate));
    }
}

function calculateEventPosition(event, eventIndex, dayEvents) {
    const conflictingEvents = dayEvents.filter((otherEvent, otherIndex) => {
        if (otherIndex === eventIndex) return false;
        
        if (event.isOvernight && otherEvent.isOvernight) {
            const timeOverlap = event.start < otherEvent.end && event.end > otherEvent.start;
            const isSameEvent = event.user_id === otherEvent.user_id && 
                               event.startRaw === otherEvent.startRaw && 
                               event.endRaw === otherEvent.endRaw &&
                               event.details === otherEvent.details &&
                               event.room_name === otherEvent.room_name &&
                               event.assignment_type === otherEvent.assignment_type &&
                               event.center_id === otherEvent.center_id &&
                               event.timestamp === otherEvent.timestamp;
            
        
            return timeOverlap && !isSameEvent;
        }
        
        if (event.isOvernight !== otherEvent.isOvernight) {
            return false;
        }
        
        const epsilon = 0.001;
        const timeOverlap = (event.start + epsilon) < otherEvent.end && (event.end - epsilon) > otherEvent.start;
        const isSameEvent = event.user_id === otherEvent.user_id && 
                           event.startRaw === otherEvent.startRaw && 
                           event.endRaw === otherEvent.endRaw &&
                           event.details === otherEvent.details &&
                           event.room_name === otherEvent.room_name &&
                           event.assignment_type === otherEvent.assignment_type &&
                           event.center_id === otherEvent.center_id &&
                           event.timestamp === otherEvent.timestamp;
        
        
        return timeOverlap && !isSameEvent;
    });

    if (conflictingEvents.length === 0) {
        return {
            left: 0,
            width: '100%',
            zIndex: 10,
            textAlign: 'top',
            isConflicting: false
        };
    }


    const allConflicting = [event, ...conflictingEvents];

    allConflicting.sort((a, b) => {
        if (a.start !== b.start) return a.start - b.start;
        if (a.user_id !== b.user_id) return (a.user_id || '').localeCompare(b.user_id || '');
        if (a.centerName !== b.centerName) return (a.centerName || '').localeCompare(b.centerName || '');
        if (a.room_name !== b.room_name) return (a.room_name || '').localeCompare(b.room_name || '');
        return (a.details || '').localeCompare(b.details || '');
    });

    const totalConflicting = allConflicting.length;
    const eventWidth = 100 / totalConflicting;
    

    const positionInConflict = allConflicting.findIndex(e =>
        e.start === event.start &&
        e.end === event.end &&
        e.centerName === event.centerName &&
        e.user_id === event.user_id &&
        e.room_name === event.room_name &&
        e.details === event.details &&
        e.title === event.title
    );

    const finalPosition = positionInConflict >= 0 ? positionInConflict : eventIndex % totalConflicting;
    const left = finalPosition * eventWidth;

    return {
        left: `${left}%`,
        width: `${eventWidth}%`,
        zIndex: 10,
        textAlign: 'top',
        isConflicting: true,
        totalConflicting: totalConflicting,
        positionInCollision: finalPosition
    };
}

function getEventColor(event) {
    const colors = USER_COLORS;

    if (event.user_id) {
        const userId = event.user_id;

        if (window.userColorMap && window.userColorMap[userId] && window.userColorMap[userId].color) {
            return window.userColorMap[userId].color;
        }

        return 'blue';
    }

    const eventString = `${event.centerName || event.title || ''}${event.room_name || ''}${event.start || ''}${event.end || ''}`;
    let hash = 0;
    for (let i = 0; i < eventString.length; i++) {
        const char = eventString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return colors[Math.abs(hash) % colors.length];
}

function processProviders(providers) {
    const userEmailMap = {};
    const allAvailability = {};
    const allUnavailability = {};

    providers.forEach(provider => {
        userEmailMap[provider.user_id] = {
            name: provider.user_name || provider.email || provider.user_id,
            color: provider.user_color || null
        };

        if (provider.availability) {
            allAvailability[provider.user_id] = provider.availability;
        } else if (provider.profile && provider.profile.availability) {
            allAvailability[provider.user_id] = provider.profile.availability;
        }

        if (provider.unavailability) {
            allUnavailability[provider.user_id] = provider.unavailability;
        } else if (provider.profile && provider.profile.unavailability) {
            allUnavailability[provider.user_id] = provider.profile.unavailability;
        }
    });

    return { userEmailMap, allAvailability, allUnavailability };
}

const Calendar = () => {
    const today = new Date();
    const apiClient = useApiClient();
    const { user } = useContext(AuthContext);
    
    const savedState = loadCalendarState();
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const urlCenters = urlParams.get('centers');
    const urlCenterSingle = urlParams.get('center_id');
    const urlUsers = urlParams.get('users');
    const urlType = urlParams.get('type');
    const urlView = urlParams.get('view');
    const urlMonth = urlParams.get('month');
    const urlDate = urlParams.get('date');
    const urlInitialMonthlyDate = urlMonth
        ? (() => { const [y, m] = urlMonth.split('-').map(Number); return (y && m) ? new Date(y, m - 1, 1) : null; })()
        : null;
    const urlInitialDailyDate = urlDate
        ? (() => { const d = new Date(urlDate); return isNaN(d) ? null : d; })()
        : null;
    // Ids are ASCII (UUIDs / emails). Strip any non-printable / private-use
    // characters that can sneak into a shared link (e.g. a stray U+E000 that
    // arrives url-encoded as %EE%80%80) so a corrupted `centers` value still
    // resolves to the real center.
    const cleanId = (s) => s.replace(/[^\x20-\x7E]/g, '');
    const splitIds = (raw) => raw.split(',').map(cleanId).filter(Boolean);
    const urlInitialCenters = urlCenters
        ? new Set(splitIds(urlCenters))
        : (urlCenterSingle ? new Set([cleanId(urlCenterSingle)]) : null);
    const urlInitialUsers = urlUsers ? new Set(splitIds(urlUsers)) : null;
    const urlInitialType = (urlType === 'all' || urlType === 'assignments' || urlType === 'shifts' || urlType === 'drafts') ? urlType : null;
    const urlIsMonthly = urlView === 'monthly' || (!urlView && !!urlMonth);
    const urlIsDaily = urlView === 'daily' || (!urlView && !!urlDate);

    const [selectedDate, setSelectedDate] = useState(savedState?.selectedDate || getSunday(today));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [centers, setCenters] = useState([]);
    const [centerMap, setCenterMap] = useState({});
    const [userMap, setUserMap] = useState({});
    const [selectedUsers, setSelectedUsers] = useState(urlInitialUsers || savedState?.selectedUsers || new Set());
    const [selectedCenters, setSelectedCenters] = useState(urlInitialCenters || savedState?.selectedCenters || new Set());
    const [isDailyView, setIsDailyView] = useState(urlView ? urlIsDaily : (savedState?.isDailyView || urlIsDaily || false));
    const [isMonthlyView, setIsMonthlyView] = useState(urlView ? urlIsMonthly : (savedState?.isMonthlyView || urlIsMonthly || false));
    const [dailyViewDate, setDailyViewDate] = useState(urlInitialDailyDate || savedState?.dailyViewDate || today);
    const [monthlyViewDate, setMonthlyViewDate] = useState(urlInitialMonthlyDate || savedState?.monthlyViewDate || today);
    const [availability, setAvailability] = useState({});
    const [unavailability, setUnavailability] = useState({});
    const [hoveredEvent, setHoveredEvent] = useState(null);
    const [assignmentTypeFilter, setAssignmentTypeFilter] = useState(urlInitialType || savedState?.assignmentTypeFilter || 'all');
    const [isMultipleSelectMode, setIsMultipleSelectMode] = useState(false);

    const [eventDetailsModal, setEventDetailsModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [editForm, setEditForm] = useState({
        user_id: "",
        center_id: "",
        room_name: "",
        date: "",
        start_time: "",
        end_time: "",
        details: "",
        assignment_type: "assignment"
    });
    const [editRooms, setEditRooms] = useState([]);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState("");
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [centersLoading, setCentersLoading] = useState(false);

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        user_id: "",
        center_id: "",
        room_name: "",
        date: "",
        start_time: "",
        end_time: "",
        details: "",
        assignment_type: "assignment",
        shift_type: "day",
        input_method: "manual",
        parse_text: "",
    });
    const [addRooms, setAddRooms] = useState([]);
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState("");

    const [assignmentSuccess, setAssignmentSuccess] = useState("");
    const [assignmentError, setAssignmentError] = useState("");

    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportForm, setReportForm] = useState({
        startDate: '',
        endDate: ''
    });
    const [reportLoading, setReportLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [reportError, setReportError] = useState("");
    const [centerSearch, setCenterSearch] = useState("");
    const [providerSearch, setProviderSearch] = useState("");
    const [selectedReportCenters, setSelectedReportCenters] = useState(new Set());
    const [selectedReportProviders, setSelectedReportProviders] = useState(new Set());

    const [draftReview, setDraftReview] = useState(null);
    const [draftReviewBusy, setDraftReviewBusy] = useState(false);
    const [draftReviewMsg, setDraftReviewMsg] = useState("");
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingRequestsLoading, setPendingRequestsLoading] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);

    const [addCenterModalOpen, setAddCenterModalOpen] = useState(false);
    const [centerForm, setCenterForm] = useState({
        name: "",
        location: "",
        type: "medical_center",
        rooms: [],
        roomName: "",
    });
    const [centerError, setCenterError] = useState("");
    const [centerLoading, setCenterLoading] = useState(false);

    const [addProviderSearch, setAddProviderSearch] = useState("");
    const [addCenterSearch, setAddCenterSearch] = useState("");
    const [editProviderSearch, setEditProviderSearch] = useState("");
    const [editCenterSearch, setEditCenterSearch] = useState("");
    const [certifiedProviders, setCertifiedProviders] = useState([]);
    const [filteredProviders, setFilteredProviders] = useState([]);
    const [datePickerModalOpen, setDatePickerModalOpen] = useState(false);
    const [selectedDateForPicker, setSelectedDateForPicker] = useState(today);

    const fetchWeeklyData = async () => {
        setLoading(true);
        setError(null);
        try {
            const sunday = getSunday(selectedDate);
            const weekStartDate = toISODate(sunday);

            const centersRes = await apiClient.get("/centers");
            const centersData = centersRes.centers || [];
            const sortedCenters = centersData.sort((a, b) => {
                const aType = a.type || 'medical_center';
                const bType = b.type || 'medical_center';

                if (aType === 'hospital' && bType !== 'hospital') return -1;
                if (bType === 'hospital' && aType !== 'hospital') return 1;

                return (a.name || '').localeCompare(b.name || '');
            });
            setCenters(sortedCenters);
            const map = {};
            sortedCenters.forEach(c => { map[c.center_id] = c.name; });
            setCenterMap(map);

            if (selectedCenters.size === 0 && selectedUsers.size === 0 && centersData.length > 0) {
                const firstCenterId = centersData[0].center_id;
                setSelectedCenters(new Set([firstCenterId]));
            }

            let allAssignments = [];
            if (selectedUsers.size > 0) {
                const selectedUserIds = Array.from(selectedUsers);
                allAssignments = await fetchWeeklyAssignmentsByUsers(selectedUserIds, weekStartDate);
            } else if (selectedCenters.size > 0) {
                const selectedCenterIds = Array.from(selectedCenters);
                allAssignments = await fetchWeeklyAssignmentsByCenters(selectedCenterIds, weekStartDate);
            } else {
                allAssignments = await fetchWeeklyAssignments(weekStartDate);
            }
            
            setAssignments(allAssignments);

            try {
                const providerResp = await apiClient.get("/user_profile", { params: { role: "provider" } });
                const providers = providerResp.users || [];
                const { userEmailMap, allAvailability, allUnavailability } = processProviders(providers);
                setUserMap(userEmailMap);
                setAvailability(allAvailability);
                setUnavailability(allUnavailability);
                window.userColorMap = userEmailMap;
            } catch (e) {
                console.warn("Failed to fetch providers:", e);
                setUserMap({});
                setAvailability({});
                setUnavailability({});
                window.userColorMap = {};
            }
        } catch (e) {
            setError("Failed to load dashboard data");
            setAssignments([]);
            setCenters([]);
            setCenterMap({});
            setUserMap({});
            setAvailability({});
            setUnavailability({});
        } finally {
            setLoading(false);
        }
    };

    const fetchDailyData = async () => {
        setLoading(true);
        setError(null);
        try {
            const dateStr = toISODate(dailyViewDate);
            const previousDay = new Date(dailyViewDate);
            previousDay.setDate(previousDay.getDate() - 1);
            const previousDayStr = toISODate(previousDay);

            const centersRes = await apiClient.get("/centers");
            const centersData = centersRes.centers || [];
            const sortedCenters = centersData.sort((a, b) => {
                const aType = a.type || 'medical_center';
                const bType = b.type || 'medical_center';

                if (aType === 'hospital' && bType !== 'hospital') return -1;
                if (bType === 'hospital' && aType !== 'hospital') return 1;

                return (a.name || '').localeCompare(b.name || '');
            });
            setCenters(sortedCenters);
            const map = {};
            sortedCenters.forEach(c => { map[c.center_id] = c.name; });
            setCenterMap(map);

            if (selectedCenters.size === 0 && selectedUsers.size === 0 && centersData.length > 0) {
                const firstCenterId = centersData[0].center_id;
                setSelectedCenters(new Set([firstCenterId]));
            }

            let allAssignments = [];
            if (selectedUsers.size > 0) {
                const selectedUserIds = Array.from(selectedUsers);
                const currentDayAssignments = await fetchAssignmentsByUser(selectedUserIds, dateStr, dateStr);
                const previousDayAssignments = await fetchAssignmentsByUser(selectedUserIds, previousDayStr, previousDayStr);
                allAssignments = [...currentDayAssignments, ...previousDayAssignments];
            } else if (selectedCenters.size > 0) {
                const selectedCenterIds = Array.from(selectedCenters);
                const currentDayAssignments = await fetchAllAssignments(selectedCenterIds, dateStr, dateStr);
                const previousDayAssignments = await fetchAllAssignments(selectedCenterIds, previousDayStr, previousDayStr);
                allAssignments = [...currentDayAssignments, ...previousDayAssignments];
            } else {
                allAssignments = [];
            }
            
            setAssignments(allAssignments);

            try {
                const providerResp = await apiClient.get("/user_profile", { params: { role: "provider" } });
                const providers = providerResp.users || [];
                const { userEmailMap, allAvailability, allUnavailability } = processProviders(providers);
                setUserMap(userEmailMap);
                setAvailability(allAvailability);
                setUnavailability(allUnavailability);
                window.userColorMap = userEmailMap;
            } catch (e) {
                console.warn("Failed to fetch providers:", e);
                setUserMap({});
                setAvailability({});
                setUnavailability({});
                window.userColorMap = {};
            }
        } catch (e) {
            setError("Failed to load dashboard data");
            setAssignments([]);
            setCenters([]);
            setCenterMap({});
            setUserMap({});
            setAvailability({});
            setUnavailability({});
        } finally {
            setLoading(false);
        }
    };

    const fetchAllAssignments = async (selectedCenterIds, startDate, endDate) => {
        const params = {
            center_ids: selectedCenterIds.join(','),
            start_date: startDate,
            end_date: endDate,
            ...((draftReview || assignmentTypeFilter === 'drafts' || assignmentTypeFilter === 'all') ? { include_drafts: 'true' } : {}),
        };

        const response = await apiClient.get("/assignments", { params });
        return response.assignments || [];
    };

    const fetchAssignmentsByUser = async (selectedUserIds, startDate, endDate) => {
        if (selectedUserIds.length === 0) return [];

        const allAssignments = [];
        for (const userId of selectedUserIds) {
            const params = {
                user_id: userId,
                start_date: startDate,
                end_date: endDate,
                ...((draftReview || assignmentTypeFilter === 'drafts' || assignmentTypeFilter === 'all') ? { include_drafts: 'true' } : {}),
            };

            const response = await apiClient.get("/assignments", { params });
            allAssignments.push(...(response.assignments || []));
        }

        return allAssignments;
    };

    const fetchWeeklyAssignments = async (weekStartDate) => {
        const params = {
            week_start: weekStartDate
        };

        const response = await apiClient.get("/assignments", { params });
        return response.assignments || [];
    };

    const fetchWeeklyAssignmentsByCenters = async (selectedCenterIds, weekStartDate) => {
        if (selectedCenterIds.length === 0) return [];
        
        const allAssignments = [];
        
        for (const centerId of selectedCenterIds) {
            try {
                const params = {
                    center_id: centerId,
                    start_date: weekStartDate,
                    end_date: getWeekEndDate(weekStartDate)
                };
                
                const response = await apiClient.get("/assignments", { params });
                allAssignments.push(...(response.assignments || []));
            } catch (error) {
                console.warn(`Failed to fetch assignments for center ${centerId}:`, error);
            }
        }
        
        return allAssignments;
    };

    const fetchWeeklyAssignmentsByUsers = async (selectedUserIds, weekStartDate) => {
        if (selectedUserIds.length === 0) return [];
        
        const allAssignments = [];
        
        for (const userId of selectedUserIds) {
            try {
                const params = {
                    user_id: userId,
                    start_date: weekStartDate,
                    end_date: getWeekEndDate(weekStartDate)
                };
                
                const response = await apiClient.get("/assignments", { params });
                allAssignments.push(...(response.assignments || []));
            } catch (error) {
                console.warn(`Failed to fetch assignments for user ${userId}:`, error);
            }
        }
        
        return allAssignments;
    };

    const fetchMonthlyData = async () => {
        setLoading(true);
        setError(null);
        try {
            const year = monthlyViewDate.getFullYear();
            const month = monthlyViewDate.getMonth() + 1;
            const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
            const endDate = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;

            const centersRes = await apiClient.get("/centers");
            const centersData = centersRes.centers || [];
            const sortedCenters = centersData.sort((a, b) => {
                const aType = a.type || 'medical_center';
                const bType = b.type || 'medical_center';

                if (aType === 'hospital' && bType !== 'hospital') return -1;
                if (bType === 'hospital' && aType !== 'hospital') return 1;

                return (a.name || '').localeCompare(b.name || '');
            });
            setCenters(sortedCenters);
            const map = {};
            sortedCenters.forEach(c => { map[c.center_id] = c.name; });
            setCenterMap(map);

            if (selectedCenters.size === 0 && selectedUsers.size === 0 && centersData.length > 0) {
                const firstCenterId = centersData[0].center_id;
                setSelectedCenters(new Set([firstCenterId]));
            }

            if (selectedUsers.size > 0) {
                const selectedUserIds = Array.from(selectedUsers);
                const allAssignments = await fetchAssignmentsByUser(selectedUserIds, startDate, endDate);
                setAssignments(allAssignments);
            } else if (selectedCenters.size > 0) {
                const selectedCenterIds = Array.from(selectedCenters);
                const allAssignments = await fetchAllAssignments(selectedCenterIds, startDate, endDate);
                setAssignments(allAssignments);
            } else {
                setAssignments([]);
            }

            try {
                const providerResp = await apiClient.get("/user_profile", { params: { role: "provider" } });
                const providers = providerResp.users || [];
                const { userEmailMap, allAvailability, allUnavailability } = processProviders(providers);
                setUserMap(userEmailMap);
                setAvailability(allAvailability);
                setUnavailability(allUnavailability);
                window.userColorMap = userEmailMap;
            } catch (e) {
                console.warn("Failed to fetch providers:", e);
                setUserMap({});
                setAvailability({});
                setUnavailability({});
                window.userColorMap = {};
            }
        } catch (e) {
            setError("Failed to load dashboard data");
            setAssignments([]);
            setCenters([]);
            setCenterMap({});
            setUserMap({});
            setAvailability({});
            setUnavailability({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isDailyView) {
            fetchDailyData();
        } else if (isMonthlyView) {
            fetchMonthlyData();
        } else {
            fetchWeeklyData();
        }
    }, [selectedDate, dailyViewDate, monthlyViewDate, isDailyView, isMonthlyView, selectedCenters, selectedUsers, assignmentTypeFilter]);

    useEffect(() => {
        if (assignmentSuccess) {
            const timer = setTimeout(() => {
                setAssignmentSuccess("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [assignmentSuccess]);

    useEffect(() => {
        if (assignmentError) {
            const timer = setTimeout(() => {
                setAssignmentError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [assignmentError]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showAddMenu && !event.target.closest('.add-menu-container')) {
                setShowAddMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAddMenu]);



    const sunday = getSunday(selectedDate);
    const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + i);
        return date;
    });

    const dayStartHour = 0;
    const dayEndHour = 24;
    const numRows = dayEndHour - dayStartHour;
    const rowHeight = 60;
    const numDays = 7;

    useEffect(() => {
        setTimeout(() => {
            let scrollContainer = document.querySelector('.relative.flex-1.w-full.bg-white.overflow-y-auto[style*="minHeight"]');
            if (!scrollContainer) {
                const containers = document.querySelectorAll('.overflow-y-auto');
                for (let container of containers) {
                    if (container.style.minHeight === '0' || container.style.minHeight === '0px') {
                        scrollContainer = container;
                        break;
                    }
                }
            }
            if (scrollContainer) {
                const scrollToY = 8 * rowHeight;
                scrollContainer.scrollTop = scrollToY;
            }
        }, 200);
    }, []);

    useEffect(() => {
        if (addForm.center_id && users.length > 0 && centers.length > 0) {
            const certified = getCertifiedProvidersForCenter(users, addForm.center_id, centers);
            setCertifiedProviders(certified);
            
            const sorted = sortProvidersWithCertifiedFirst(users, certified);
            setFilteredProviders(sorted);
        } else {
            setCertifiedProviders([]);
            setFilteredProviders(users);
        }
    }, [addForm.center_id, users, centers]);

    useEffect(() => {
        if (users.length > 0) {
            setFilteredProviders(users);
        }
    }, [users]);

    useEffect(() => {
        if (editForm.center_id && users.length > 0 && centers.length > 0) {
            const certified = getCertifiedProvidersForCenter(users, editForm.center_id, centers);
            setCertifiedProviders(certified);
            
            const sorted = sortProvidersWithCertifiedFirst(users, certified);
            setFilteredProviders(sorted);
        } else {
            setCertifiedProviders([]);
            setFilteredProviders(users);
        }
    }, [editForm.center_id, users, centers]);

    useEffect(() => {
        if (reportModalOpen && users.length > 0) {
            const allProviderIds = new Set(users.map(user => user.user_id));
            setSelectedReportProviders(allProviderIds);
        }
    }, [reportModalOpen, users]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const request_id = params.get('request_id');
        const center_id = cleanId(params.get('center_id') || '');
        const month = params.get('month');
        if (request_id && center_id && month && params.get('assignment_type') === 'draft') {
            setDraftReview({ request_id, center_id, month });
            setSelectedCenters(new Set([center_id]));
            const [y, m] = month.split('-').map(Number);
            const monthDate = new Date(y, m - 1, 1);
            setMonthlyViewDate(monthDate);
            setIsMonthlyView(true);
            setIsDailyView(false);
        }
    }, []);

    const fetchPendingRequests = async () => {
        setPendingRequestsLoading(true);
        try {
            const res = await apiClient.get('/assignments', { params: { list: 'schedule_requests' } });
            setPendingRequests(res.requests || []);
        } catch (e) {
            console.warn('fetch pending requests:', e);
            setPendingRequests([]);
        } finally {
            setPendingRequestsLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openDraftReview = (req) => {
        if (!req || !req.request_id || !req.center_id || !req.month) return;
        setDraftReview({ request_id: req.request_id, center_id: req.center_id, month: req.month });
        setSelectedCenters(new Set([req.center_id]));
        const [y, m] = req.month.split('-').map(Number);
        const monthDate = new Date(y, m - 1, 1);
        setMonthlyViewDate(monthDate);
        setIsMonthlyView(true);
        setIsDailyView(false);
        const params = new URLSearchParams(window.location.search);
        params.set('request_id', req.request_id);
        params.set('center_id', req.center_id);
        params.set('month', req.month);
        params.set('assignment_type', 'draft');
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    };

    const handleApproveDraft = async () => {
        if (!draftReview) return;
        setDraftReviewBusy(true);
        setDraftReviewMsg("");
        try {
            const res = await apiClient.post('/assignments', { action: 'approve_draft', ...draftReview });
            let msg = `Approved — ${res.promoted || 0} assignments published.`;
            if (res.conflicts_text) {
                msg += `\n\n${res.conflicts_text}`;
            }
            setDraftReviewMsg(msg);
            setDraftReview(null);
            window.history.replaceState({}, '', '/calendar');
            await fetchPendingRequests();
            if (isMonthlyView) await fetchMonthlyData();
            else await fetchWeeklyData();
        } catch (e) {
            console.error('approve draft:', e);
            setDraftReviewMsg('Failed to approve draft.');
        } finally {
            setDraftReviewBusy(false);
        }
    };

    const handleRejectDraft = async () => {
        if (!draftReview) return;
        if (!window.confirm('Reject this draft? All its assignments will be deleted.')) return;
        setDraftReviewBusy(true);
        setDraftReviewMsg("");
        try {
            const res = await apiClient.post('/assignments', { action: 'reject_draft', ...draftReview });
            setDraftReviewMsg(`Rejected — ${res.deleted || 0} draft rows deleted.`);
            setDraftReview(null);
            window.history.replaceState({}, '', '/calendar');
            await fetchPendingRequests();
            if (isMonthlyView) await fetchMonthlyData();
            else await fetchWeeklyData();
        } catch (e) {
            console.error('reject draft:', e);
            setDraftReviewMsg('Failed to reject draft.');
        } finally {
            setDraftReviewBusy(false);
        }
    };

    useEffect(() => {
        saveCalendarState({
            selectedUsers,
            selectedCenters,
            isDailyView,
            isMonthlyView,
            dailyViewDate,
            monthlyViewDate,
            assignmentTypeFilter,
            selectedDate
        });
    }, [selectedUsers, selectedCenters, isDailyView, isMonthlyView, dailyViewDate, monthlyViewDate, assignmentTypeFilter, selectedDate]);

    useEffect(() => {
        if (draftReview) return;
        const existing = new URLSearchParams(window.location.search);
        const params = new URLSearchParams();
        if (existing.get('request_id')) params.set('request_id', existing.get('request_id'));
        if (existing.get('assignment_type')) params.set('assignment_type', existing.get('assignment_type'));
        if (selectedCenters.size > 0) params.set('centers', Array.from(selectedCenters).join(','));
        if (selectedUsers.size > 0) params.set('users', Array.from(selectedUsers).join(','));
        params.set('type', assignmentTypeFilter);
        const view = isDailyView ? 'daily' : (isMonthlyView ? 'monthly' : 'weekly');
        params.set('view', view);
        if (isMonthlyView && monthlyViewDate) {
            const m = `${monthlyViewDate.getFullYear()}-${String(monthlyViewDate.getMonth() + 1).padStart(2, '0')}`;
            params.set('month', m);
        } else if (isDailyView && dailyViewDate) {
            params.set('date', toISODate(dailyViewDate));
        }
        const newSearch = params.toString();
        if (newSearch !== window.location.search.replace(/^\?/, '')) {
            window.history.replaceState({}, '', `${window.location.pathname}?${newSearch}`);
        }
    }, [selectedCenters, selectedUsers, assignmentTypeFilter, isDailyView, isMonthlyView, monthlyViewDate, dailyViewDate, draftReview]);

    const createAssignmentEvents = (assignments) => {
        const events = [];


        const assignmentIds = new Set();
        const uniqueAssignments = [];
        assignments.forEach(appt => {
            const id = `${appt.user_id}-${appt.center_id}-${appt.start_time}-${appt.end_time}-${appt.date}-${appt.details || ''}-${appt.room_name || ''}-${appt.assignment_type || ''}-${appt.timestamp || ''}`;
            if (!assignmentIds.has(id)) {
                assignmentIds.add(id);
                uniqueAssignments.push(appt);
            } 
        });
        
        if (assignments.length !== uniqueAssignments.length) {
            console.log(`Removed ${assignments.length - uniqueAssignments.length} duplicate assignments`);
        }

        let assignmentsToProcess = uniqueAssignments;
        if (isDailyView) {
            const previousDay = new Date(dailyViewDate);
            previousDay.setDate(previousDay.getDate() - 1);
            const previousDayStr = toISODate(previousDay);
            
            const previousDayAssignments = assignments.filter(appt => appt.date === previousDayStr);
            const currentDayAssignments = assignments.filter(appt => appt.date === toISODate(dailyViewDate));
            const combinedAssignments = [...uniqueAssignments];
            
            const combinedIds = new Set();
            const finalUniqueAssignments = [];
            combinedAssignments.forEach(appt => {
                const id = `${appt.user_id}-${appt.center_id}-${appt.start_time}-${appt.end_time}-${appt.date}-${appt.details || ''}-${appt.room_name || ''}-${appt.assignment_type || ''}-${appt.timestamp || ''}`;
                if (!combinedIds.has(id)) {
                    combinedIds.add(id);
                    finalUniqueAssignments.push(appt);
                } 
            });
            
            assignmentsToProcess = finalUniqueAssignments;
        }

        assignmentsToProcess
            .filter(appt => {
                if (selectedUsers.size > 0 && !selectedUsers.has(appt.user_id)) {
                    return false;
                }
                if (selectedCenters.size > 0 && !selectedCenters.has(appt.center_id)) {
                    return false;
                }
                if (appt.assignment_type === 'draft') {
                    if (draftReview && appt.request_id === draftReview.request_id) {
                        // show drafts under active review
                    } else if (assignmentTypeFilter !== 'drafts' && assignmentTypeFilter !== 'all') {
                        return false;
                    }
                } else if (assignmentTypeFilter === 'drafts') {
                    return false;
                } else if (assignmentTypeFilter === 'assignments' && appt.assignment_type === 'shift') {
                    return false;
                } else if (assignmentTypeFilter === 'shifts' && appt.assignment_type !== 'shift') {
                    return false;
                }
                
                if (isDailyView && appt.isOvernight) {
                    const currentDateStr = toISODate(dailyViewDate);
                    const previousDate = new Date(dailyViewDate);
                    previousDate.setDate(previousDate.getDate() - 1);
                    const previousDateStr = toISODate(previousDate);
                    
                    if (appt.date === currentDateStr) {
                        return false;
                    }
                }
                
                return true;
            })
            .forEach(appt => {
                let start = 9, end = 10;
                if (appt.start_time && appt.end_time) {
                    const [sh, sm] = appt.start_time.split(":").map(Number);
                    const [eh, em] = appt.end_time.split(":").map(Number);
                    start = sh + (sm || 0) / 60;
                    end = eh + (em || 0) / 60;
                } else if (appt.timestamp) {
                    const dt = new Date(appt.timestamp);
                    start = dt.getHours() + dt.getMinutes() / 60;
                    end = start + 1;
                }

                const centerName = centerMap[appt.center_id] || appt.center_id;
                const userEmail = userMap[appt.user_id]?.name || appt.user_id;

                const isOvernight = end < start && start >= 18;

                if (isOvernight && !isMonthlyView) {
                    if (isDailyView) {
                        const currentDateStr = toISODate(dailyViewDate);
                        const previousDate = new Date(dailyViewDate);
                        previousDate.setDate(previousDate.getDate() - 1);
                        const previousDateStr = toISODate(previousDate);
                        
                        if (appt.date === previousDateStr) {
                            events.push({
                                date: currentDateStr,
                                start: 0,
                                end,
                                centerName,
                                room_name: appt.room_name,
                                details: appt.details,
                                user_id: appt.user_id,
                                userEmail,
                                color: "border-blue-400",
                                text: "text-blue-700",
                                startRaw: appt.start_time,
                                endRaw: appt.end_time,
                                assignment_type: appt.assignment_type,
                                request_id: appt.request_id,
                                center_id: appt.center_id,
                                timestamp: appt.timestamp,
                                isOvernight: true,
                                isOvernightPart: 'second'
                            });
                        } else if (appt.date === currentDateStr) {
                            events.push({
                                date: appt.date,
                                start,
                                end: 24,
                                centerName,
                                room_name: appt.room_name,
                                details: appt.details,
                                user_id: appt.user_id,
                                userEmail,
                                color: "border-blue-400",
                                text: "text-blue-700",
                                startRaw: appt.start_time,
                                endRaw: appt.end_time,
                                assignment_type: appt.assignment_type,
                                request_id: appt.request_id,
                                center_id: appt.center_id,
                                timestamp: appt.timestamp,
                                isOvernight: true,
                                isOvernightPart: 'first'
                            });
                        }
                    } else {
                        events.push({
                            date: appt.date,
                            start,
                            end: 24,
                            centerName,
                            room_name: appt.room_name,
                            details: appt.details,
                            user_id: appt.user_id,
                            userEmail,
                            color: "border-blue-400",
                            text: "text-blue-700",
                            startRaw: appt.start_time,
                            endRaw: appt.end_time,
                            assignment_type: appt.assignment_type,
                            request_id: appt.request_id,
                            center_id: appt.center_id,
                            timestamp: appt.timestamp,
                            isOvernight: true,
                            isOvernightPart: 'first'
                        });

                        const nextDate = new Date(appt.date);
                        nextDate.setDate(nextDate.getDate() + 1);
                        const nextDateStr = toISODate(nextDate);

                        events.push({
                            date: nextDateStr,
                            start: 0,
                            end,
                            centerName,
                            room_name: appt.room_name,
                            details: appt.details,
                            user_id: appt.user_id,
                            userEmail,
                            color: "border-blue-400",
                            text: "text-blue-700",
                            startRaw: appt.start_time,
                            endRaw: appt.end_time,
                            assignment_type: appt.assignment_type,
                            request_id: appt.request_id,
                            center_id: appt.center_id,
                            timestamp: appt.timestamp,
                            isOvernight: true,
                            isOvernightPart: 'second'
                        });
                    }
                } else {
                    const event = {
                        date: appt.date,
                        start,
                        end,
                        centerName,
                        room_name: appt.room_name,
                        details: appt.details,
                        user_id: appt.user_id,
                        userEmail,
                        color: "border-blue-400",
                        text: "text-blue-700",
                        startRaw: appt.start_time,
                        endRaw: appt.end_time,
                        assignment_type: appt.assignment_type,
                        request_id: appt.request_id,
                        center_id: appt.center_id,
                        timestamp: appt.timestamp,
                        isOvernight: false
                    };
                    events.push(event);
                }
            });

        const uniqueEvents = [];
        const eventIds = new Set();
        const userDayEvents = new Map();
        
        events.forEach(event => {
            const eventId = `${event.user_id}-${event.date}-${event.start}-${event.end}-${event.startRaw}-${event.endRaw}-${event.isOvernightPart || 'regular'}-${event.details || ''}-${event.room_name || ''}-${event.assignment_type || ''}-${event.center_id || ''}-${event.timestamp || ''}`;
            const userDayKey = `${event.user_id}-${event.date}`;
            
            if (!eventIds.has(eventId)) {
                eventIds.add(eventId);
                
                if (!userDayEvents.has(userDayKey)) {
                    userDayEvents.set(userDayKey, []);
                }
                userDayEvents.get(userDayKey).push(event);
                
                uniqueEvents.push(event);
            }
        });

        return uniqueEvents;
    };

    const assignmentEvents = createAssignmentEvents(assignments);


    const weekRange = `${formatDateFull(weekDates[0])} - ${formatDateFull(weekDates[6])}`;

    const weekISO = weekDates.map(toISODate);
    const weekNumber = Math.ceil((new Date(weekDates[0]).getDate() - 1 + new Date(weekDates[0]).getDay()) / 7);
    const isOddWeek = weekNumber % 2 === 1;
    const isEvenWeek = weekNumber % 2 === 0;

    const selectedUserIds = Array.from(selectedUsers);
    const selectedCenterIds = Array.from(selectedCenters);

    const availEvents = [];
    const unavailEvents = [];

    const usersToProcess = isDailyView ? Object.keys(userMap) :
        (selectedUserIds.length > 0 ? selectedUserIds : []);

    const showAvailabilityData = selectedUserIds.length === 1;

    usersToProcess.forEach(userId => {
        const userAvail = availability[userId] || [];
        const userUnavail = unavailability[userId] || [];

        if (showAvailabilityData) {
            userAvail.forEach(a => {
                let days = [];
                const orig = new Date(a.date);
                weekDates.forEach((d, idx) => {
                    if (
                        (a.repeat === "none" && localDateString(d) === a.date) ||
                        (a.repeat === "weekly" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig)) ||
                        (a.repeat === "odd" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isOddWeek) ||
                        (a.repeat === "even" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isEvenWeek)
                    ) {
                        days.push({
                            date: localDateString(d),
                            start: a.allDay ? 9 : timeToDecimal(a.start),
                            end: a.allDay ? 17 : timeToDecimal(a.end),
                            title: "Available",
                            color: "border-green-400 bg-green-100",
                            text: "text-green-700",
                            isAvailable: true,
                            originalEvent: a,
                            startRaw: a.start,
                            endRaw: a.end,
                            repeat: a.repeat,
                            allDay: a.allDay,
                            user_id: userId,
                            userEmail: userMap[userId]?.name || userId,
                        });
                    }
                });
                availEvents.push(...days);
            });

            userUnavail.forEach(u => {
                let days = [];
                const orig = new Date(u.date);
                weekDates.forEach((d, idx) => {
                    if (
                        (u.repeat === "none" && localDateString(d) === u.date) ||
                        (u.repeat === "weekly" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig)) ||
                        (u.repeat === "odd" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isOddWeek) ||
                        (u.repeat === "even" && d.getDay() === orig.getDay() && localDateString(d) >= localDateString(orig) && isEvenWeek)
                    ) {
                        days.push({
                            date: localDateString(d),
                            start: u.allDay ? 9 : timeToDecimal(u.start),
                            end: u.allDay ? 17 : timeToDecimal(u.end),
                            title: "Not available",
                            color: "border-red-300 bg-red-100",
                            text: "text-red-700",
                            isUnavailable: true,
                            originalEvent: u,
                            startRaw: u.start,
                            endRaw: u.end,
                            repeat: u.repeat,
                            allDay: u.allDay,
                            user_id: userId,
                            userEmail: userMap[userId]?.name || userId,
                        });
                    }
                });
                unavailEvents.push(...days);
            });
        }
    });

    const allEvents = [
        ...assignmentEvents,
        ...(showAvailabilityData ? [...availEvents, ...unavailEvents] : []),
    ];

    const handleUserSelect = (userId) => {
        setSelectedUsers(prev => {
            if (isMultipleSelectMode) {
                const newSet = new Set(prev);
                if (newSet.has(userId)) {
                    newSet.delete(userId);
                } else {
                    newSet.add(userId);
                }
                setSelectedCenters(new Set());
                return newSet;
            } else {
                if (prev.has(userId)) {
                    return new Set();
                } else {
                    setSelectedCenters(new Set());
                    return new Set([userId]);
                }
            }
        });
    };

    const handleCenterSelect = (centerId) => {
        setSelectedCenters(prev => {
            if (isMultipleSelectMode) {
                const newSet = new Set(prev);
                if (newSet.has(centerId)) {
                    if (newSet.size > 1) {
                        newSet.delete(centerId);
                    }
                } else {
                    newSet.add(centerId);
                }
                return newSet;
            } else {
                if (prev.has(centerId)) {
                    return new Set();
                } else {
                    return new Set([centerId]);
                }
            }
        });
    };

    const clearAllFilters = () => {
        setSelectedUsers(new Set());
        if (centers.length > 0) {
            setSelectedCenters(new Set([centers[0].center_id]));
        } else {
            setSelectedCenters(new Set());
        }
        setAssignmentTypeFilter('all');
    };

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setEventDetailsModal(true);
    };

    const handleEditClick = (event) => {
        setEditingAssignment(event);
        setEditForm({
            user_id: event.user_id || "",
            center_id: event.center_id || "",
            room_name: event.room_name || "",
            date: event.date || "",
            start_time: event.startRaw || "",
            end_time: event.endRaw || "",
            details: event.details || "",
            assignment_type: event.assignment_type || "assignment"
        });
        setEditModalOpen(true);
        setEventDetailsModal(false);
        setAssignmentSuccess("");
        setAssignmentError("");
        setEditProviderSearch("");
        setEditCenterSearch("");
        fetchEditRooms(event.center_id);
        fetchUsers();
        fetchCenters();
    };

    const fetchEditRooms = async (centerId) => {
        if (!centerId) return;
        try {
            const resp = await apiClient.get(`/centers?center_id=${centerId}`);
            setEditRooms(resp.center?.rooms || []);
        } catch (e) {
            console.warn(`Failed to fetch rooms for center ${centerId}:`, e);
            setEditRooms([]);
        }
    };

    const fetchAddRooms = async (centerId) => {
        if (!centerId) return;
        try {
            const resp = await apiClient.get(`/centers?center_id=${centerId}`);
            setAddRooms(resp.center?.rooms || []);
        } catch (e) {
            console.warn(`Failed to fetch rooms for center ${centerId}:`, e);
            setAddRooms([]);
        }
    };

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const resp = await apiClient.get("/user_profile", { params: { role: "provider" } });
            const providers = resp.users || [];
            setUsers(providers);
            
            const { userEmailMap } = processProviders(providers);
            setUserMap(userEmailMap);
        } catch (e) {
            console.warn("Failed to fetch users:", e);
            setUsers([]);
            setUserMap({});
        } finally {
            setUsersLoading(false);
        }
    };

    const fetchCenters = async () => {
        setCentersLoading(true);
        try {
            const resp = await apiClient.get("/centers");
            const centersData = resp.centers || [];
            const sortedCenters = centersData.sort((a, b) => {
                const aType = a.type || 'medical_center';
                const bType = b.type || 'medical_center';

                if (aType === 'hospital' && bType !== 'hospital') return -1;
                if (bType === 'hospital' && aType !== 'hospital') return 1;

                return (a.name || '').localeCompare(b.name || '');
            });
            setCenters(sortedCenters);
            
            const map = {};
            sortedCenters.forEach(c => { map[c.center_id] = c.name; });
            setCenterMap(map);
        } catch (e) {
            console.warn("Failed to fetch centers:", e);
            setCenters([]);
            setCenterMap({});
        } finally {
            setCentersLoading(false);
        }
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        if (editingAssignment && editingAssignment.isDraft && name === 'center_id') {
            return;
        }
        setEditForm(prev => ({ ...prev, [name]: value }));

        if (name === 'center_id') {
            fetchEditRooms(value);
            
            if (value) {
                const certified = getCertifiedProvidersForCenter(users, value, centers);
                setCertifiedProviders(certified);
                
                const sorted = sortProvidersWithCertifiedFirst(users, certified);
                setFilteredProviders(sorted);
            } else {
                setCertifiedProviders([]);
                setFilteredProviders(users);
            }
        }
    };

    useEffect(() => {
        if (editModalOpen && editingAssignment && editingAssignment.isDraft && editForm.user_id) {
            const existing = users.find(u => u.user_id === editForm.user_id);
            if (existing) {
                setUserMap(prev => ({
                    ...prev,
                    [existing.user_id]: {
                        name: existing.user_name || existing.email || existing.user_id,
                        color: existing.user_color || null
                    }
                }));
            }
        }
    }, [editForm.user_id, editModalOpen, editingAssignment, users]);

    const handleAddFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;

        setAddForm(prev => {
            const updated = { ...prev, [name]: newValue };

            if (name === 'assignment_type') {
                if (value === 'shift') {
                    updated.start_time = "07:00";
                    updated.end_time = "15:00";
                    updated.shift_type = "day";
                } else {
                    updated.start_time = "";
                    updated.end_time = "";
                }
            }

            if (name === 'shift_type') {
                switch (value) {
                    case 'day':
                        updated.start_time = "07:00";
                        updated.end_time = "15:00";
                        break;
                    case 'eve':
                        updated.start_time = "15:00";
                        updated.end_time = "23:00";
                        break;
                    case 'night':
                        updated.start_time = "23:00";
                        updated.end_time = "07:00";
                        break;
                    default:
                        break;
                }
            }

            return updated;
        });

        if (name === 'center_id') {
            fetchAddRooms(value);
            
            if (value) {
                const certified = getCertifiedProvidersForCenter(users, value, centers);
                setCertifiedProviders(certified);
                
                const sorted = sortProvidersWithCertifiedFirst(users, certified);
                setFilteredProviders(sorted);
            } else {
                setCertifiedProviders([]);
                setFilteredProviders(users);
            }
        }

    };

    const handleAdd = async () => {
        const errors = [];
        if (!addForm.center_id.trim()) {
            errors.push("Center is required");
        }
        if (!addForm.date.trim()) {
            errors.push("Date is required");
        }
        if (!addForm.start_time) {
            errors.push("Start time is required");
        }
        if (!addForm.end_time) {
            errors.push("End time is required");
        }
        if (!addForm.user_id) {
            errors.push("User is required");
        }


        if (errors.length > 0) {
            setAddError(errors.join(", "));
            return;
        }

        setAddLoading(true);
        setAddError("");

        try {
            await apiClient.post("/assignments", {
                user_id: addForm.user_id,
                center_id: addForm.center_id.trim(),
                room_name: addForm.room_name || "",
                date: addForm.date.trim(),
                start_time: addForm.start_time,
                end_time: addForm.end_time,
                details: addForm.details.trim(),
                assignment_type: addForm.assignment_type,
            });

            if (isDailyView) {
                await fetchDailyData();
            } else if (isMonthlyView) {
                await fetchMonthlyData();
            } else {
                await fetchWeeklyData();
            }

            setAddForm({
                user_id: "",
                center_id: "",
                room_name: "",
                date: "",
                start_time: "",
                end_time: "",
                details: "",
                assignment_type: "assignment",
                shift_type: "day",
            });
            setAddRooms([]);
            setAddModalOpen(false);
            setAssignmentSuccess("Assignment added successfully!");
            setAssignmentError("");
        } catch (e) {
            setAddError("Failed to add assignment");
            setAssignmentError("Failed to add assignment");
            setAssignmentSuccess("");
            console.error("Error adding assignment:", e);
        } finally {
            setAddLoading(false);
        }
    };

    const openAddModal = () => {
        const today = new Date().toISOString().split('T')[0];

        setAddModalOpen(true);
        setAddError("");
        setAssignmentSuccess("");
        setAssignmentError("");

        setAddForm({
            user_id: "",
            center_id: "",
            room_name: "",
            date: today,
            start_time: "",
            end_time: "",
            details: "",
            assignment_type: "assignment",
            shift_type: "day",
            input_method: "manual",
            parse_text: "",
        });

        setAddProviderSearch("");
        setAddCenterSearch("");

        fetchCenters();
        fetchUsers();
    };

    const openReportModal = () => {
        const today = new Date();
        const endDate = new Date(today);
        endDate.setMonth(endDate.getMonth() + 1);

        setReportModalOpen(true);
        setReportError("");
        setReportData(null);
        setCenterSearch("");
        setProviderSearch("");
        setSelectedReportCenters(new Set());
        setSelectedReportProviders(new Set());

        setReportForm({
            startDate: today.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        });

        fetchCenters();
        fetchUsers();
    };

    const handleReportFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setReportForm(prev => {
            const updated = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            
            if (name === 'startDate' && value) {
                const startDate = new Date(value);
                const endDate = new Date(startDate);
                endDate.setMonth(endDate.getMonth() + 1);
                updated.endDate = endDate.toISOString().split('T')[0];
            }
            
            return updated;
        });
        setReportError("");
        
        if (reportData) {
            setReportData(null);
        }
    };

    const canGenerateReport = () => {
        return reportForm.startDate && reportForm.endDate && selectedReportProviders.size > 0;
    };

        const generateReport = async () => {
        if (!canGenerateReport()) {
            setReportError("Please fill in all required fields");
            return;
        }

        if (Object.keys(userMap).length === 0 || Object.keys(centerMap).length === 0) {
            setReportError("Please wait for user and center data to load before generating report");
            return;
        }

        setReportLoading(true);
        setReportError("");

        try {
            let allShifts = [];
            const providerIds = Array.from(selectedReportProviders);
            
            for (const providerId of providerIds) {
                const url = `/assignments?assignment_type=shift&user_id=${providerId}&start_date=${reportForm.startDate}&end_date=${reportForm.endDate}`;
                const response = await apiClient.get(url);
                const shifts = response.assignments || [];
                allShifts.push(...shifts);
            }
            
            let filteredShifts = allShifts.filter(shift => shift.assignment_type === 'shift');
            
            const groupedShifts = {};
            filteredShifts.forEach(shift => {
                const providerId = shift.user_id;
                if (!groupedShifts[providerId]) {
                    groupedShifts[providerId] = [];
                }
                groupedShifts[providerId].push(shift);
            });
            
            Object.keys(groupedShifts).forEach(providerId => {
                groupedShifts[providerId].sort((a, b) => a.date.localeCompare(b.date));
            });
            
            const newReportData = {
                shifts: filteredShifts,
                groupedShifts: groupedShifts,
                startDate: reportForm.startDate,
                endDate: reportForm.endDate,
                userIds: Array.from(selectedReportProviders),
                totalShifts: filteredShifts.length
            };

            setReportData(newReportData);

        } catch (error) {
            console.error('Error generating report:', error);
            setReportError(error.response?.data?.error || error.message || 'Failed to generate report');
        } finally {
            setReportLoading(false);
        }
    };



    const handlePrintCalendar = () => {
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        
        if (!printWindow) {
            alert('Please allow popups to print the calendar');
            return;
        }

        const selectedCenterNames = Array.from(selectedCenters).map(id => centerMap[id] || id).join(', ');
        const selectedProviderNames = Array.from(selectedUsers).map(id => userMap[id]?.name || userMap[id]?.email || id).join(', ');
        
        const title = `Monthly Calendar - ${monthlyViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
        const subtitle = [];
        if (selectedCenterNames) subtitle.push(`Centers: ${selectedCenterNames}`);
        if (selectedProviderNames) subtitle.push(`Providers: ${selectedProviderNames}`);
        if (subtitle.length === 0) subtitle.push('All Centers and Providers');
        
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${title}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 20px;
                        background: white;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 20px;
                        border-bottom: 2px solid #333;
                        padding-bottom: 10px;
                    }
                    .title {
                        font-size: 24px;
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    .subtitle {
                        font-size: 14px;
                        color: #666;
                    }
                    .calendar {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    .calendar th {
                        background: #f8f9fa;
                        border: 1px solid #dee2e6;
                        padding: 10px;
                        text-align: center;
                        font-weight: bold;
                    }
                    .calendar td {
                        border: 1px solid #dee2e6;
                        vertical-align: top;
                        height: 120px;
                        width: 14.28%;
                        padding: 5px;
                    }
                    .day-number {
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    .other-month {
                        background: #f8f9fa;
                        color: #6c757d;
                    }
                    .today {
                        background: #e3f2fd;
                        border: 2px solid #2196f3;
                    }
                    .assignment {
                        font-size: 10px;
                        margin-bottom: 2px;
                        padding: 2px;
                        border-radius: 2px;
                        border-left: 3px solid #007bff;
                        background: #f8f9fa;
                        word-wrap: break-word;
                    }
                    .time {
                        font-weight: bold;
                        font-size: 9px;
                    }
                    .provider {
                        font-size: 9px;
                        color: #495057;
                    }
                    .center {
                        font-size: 9px;
                        color: #6c757d;
                    }
                    .shift-type {
                        font-size: 8px;
                        background: #e9ecef;
                        padding: 1px 3px;
                        border-radius: 1px;
                        margin-top: 1px;
                    }
                    @media print {
                        body { margin: 0; }
                        .header { margin-bottom: 15px; }
                        .calendar td { height: 100px; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="title">${title}</div>
                    <div class="subtitle">${subtitle.join(' | ')}</div>
                </div>
                <table class="calendar">
                    <thead>
                        <tr>
                            <th>Sun</th>
                            <th>Mon</th>
                            <th>Tue</th>
                            <th>Wed</th>
                            <th>Thu</th>
                            <th>Fri</th>
                            <th>Sat</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${(() => {
                            const days = getCalendarDays(monthlyViewDate);
                            const rows = [];
                            for (let i = 0; i < days.length; i += 7) {
                                const weekDays = days.slice(i, i + 7);
                                const row = weekDays.map(date => {
                                    const dayAssignments = getAssignmentsForDate(date, assignmentEvents);
                                    const isCurrentMonthDay = isCurrentMonth(date, monthlyViewDate);
                                    const isTodayDate = isToday(date);
                                    
                                    let dayClass = '';
                                    if (!isCurrentMonthDay) dayClass = 'other-month';
                                    if (isTodayDate) dayClass = 'today';
                                    
                                    let assignmentsHtml = '';
                                    if (dayAssignments.length > 0) {
                                        const sortedAssignments = dayAssignments.sort((a, b) => {
                                            const timeA = timeToDecimal(a.startRaw);
                                            const timeB = timeToDecimal(b.startRaw);
                                            return timeA - timeB;
                                        });
                                        assignmentsHtml = sortedAssignments.map(appt => {
                                            const timeStr = `${formatTimeStr(appt.startRaw)} - ${formatTimeStr(appt.endRaw)}`;
                                            const providerStr = appt.userEmail || appt.user_id;
                                            const centerStr = appt.centerName || centerMap[appt.center_id];
                                            const shiftTypeStr = appt.assignment_type === 'shift' ? getShiftType(appt.startRaw, appt.endRaw) : '';
                                            
                                            return `
                                                <div class="assignment">
                                                    <div class="time">${timeStr}</div>
                                                    <div class="provider">${providerStr}</div>
                                                    <div class="center">${centerStr}</div>
                                                    ${shiftTypeStr ? `<div class="shift-type">${shiftTypeStr}</div>` : ''}
                                                </div>
                                            `;
                                        }).join('');
                                    }
                                    
                                    return `
                                        <td class="${dayClass}">
                                            <div class="day-number">${date.getDate()}</div>
                                            ${assignmentsHtml}
                                        </td>
                                    `;
                                }).join('');
                                rows.push(`<tr>${row}</tr>`);
                            }
                            return rows.join('');
                        })()}
                    </tbody>
                </table>
            </body>
            </html>
        `;
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        };
    };

    const downloadCSV = () => {
        if (!reportData || !reportData.groupedShifts) {
            return;
        }

        let csvContent = '';
        
        csvContent += 'Provider Report\n';
        csvContent += `Date Range: ${reportData.startDate} to ${reportData.endDate}\n\n`;
        
        Object.keys(reportData.groupedShifts).forEach(providerId => {
            const providerName = userMap[providerId]?.name || providerId;
            const shifts = reportData.groupedShifts[providerId];
            
            if (shifts.length > 0) {
                csvContent += `Provider: ${providerName}\n`;
                csvContent += 'Date,Provider,Center,Room,Start Time,End Time,Shift Type\n';
                
                shifts.forEach(shift => {
                    const centerName = centerMap[shift.center_id] || shift.center_id;
                    const shiftType = getShiftType(shift.start_time, shift.end_time);
                    
                    csvContent += [
                        shift.date,
                        providerName,
                        centerName,
                        shift.room_name || '',
                        formatTimeStr(shift.start_time),
                        formatTimeStr(shift.end_time),
                        shiftType
                    ].join(',') + '\n';
                });
                
                csvContent += '\n';
            }
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `provider_report_${reportForm.startDate}_to_${reportForm.endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCenterFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCenterForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const handleAddCenterRoom = () => {
        const name = centerForm.roomName.trim();
        if (!name) return;
        setCenterForm(f => ({
            ...f,
            rooms: f.rooms.includes(name) ? f.rooms : [...f.rooms, name],
            roomName: "",
        }));
    };

    const handleRemoveCenterRoom = (idx) => {
        setCenterForm(f => ({
            ...f,
            rooms: f.rooms.filter((_, i) => i !== idx),
        }));
    };

    const handleAddCenter = async () => {
        const errors = [];
        if (!centerForm.name.trim()) {
            errors.push("Center name is required");
        }
        if (!centerForm.location.trim()) {
            errors.push("Location is required");
        }

        if (errors.length > 0) {
            setCenterError(errors.join(", "));
            return;
        }
        setCenterLoading(true);
        setCenterError("");
        try {
            const center_id = uuidv4();
            await apiClient.post("/centers", {
                action: "add_center",
                center_id,
                name: centerForm.name.trim(),
                location: centerForm.location.trim() || "",
                type: centerForm.type,
                rooms: centerForm.rooms,
            });
            await fetchCenters();
            setCenterForm({
                name: "",
                location: "",
                type: "medical_center",
                rooms: [],
                roomName: "",
            });
            setAddCenterModalOpen(false);
        } catch (e) {
            setCenterError("Failed to add center");
        } finally {
            setCenterLoading(false);
        }
    };

    const openAddCenterModal = () => {
        setAddCenterModalOpen(true);
        setCenterError("");
    };

    const handleEdit = async () => {
        if (!editingAssignment) return;

        setEditLoading(true);
        setEditError("");

        try {
            const requestBody = {
                user_id: editingAssignment.user_id,
                timestamp: editingAssignment.timestamp,
                center_id: editForm.center_id,
                room_name: editForm.room_name,
                date: editForm.date,
                start_time: editForm.start_time,
                end_time: editForm.end_time,
                details: editForm.details,
                assignment_type: editingAssignment.assignment_type || 'assignment'
            };

            if (editForm.user_id !== editingAssignment.user_id) {
                requestBody.new_user_id = editForm.user_id;
            }

            const response = await apiClient.put("/assignments", requestBody);

            if (response.assignment) {
                if (isDailyView) {
                    await fetchDailyData();
                } else if (isMonthlyView) {
                    await fetchMonthlyData();
                } else {
                    await fetchWeeklyData();
                }

                setEditModalOpen(false);
                setEditingAssignment(null);
                setEditForm({
                    user_id: "",
                    center_id: "",
                    room_name: "",
                    date: "",
                    start_time: "",
                    end_time: "",
                    details: "",
                    assignment_type: "assignment"
                });
                setEditRooms([]);
                setEditProviderSearch("");
                setEditCenterSearch("");
                setAssignmentSuccess("Assignment updated successfully!");
                setAssignmentError("");
            }
        } catch (e) {
            setEditError("Failed to update assignment");
            setAssignmentError("Failed to update assignment");
            setAssignmentSuccess("");
            console.error("Error updating assignment:", e);
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete this assignment?`)) {
            return;
        }
        setEditLoading(true);
        setEditError("");
        try {
            // Check if this is a draft assignment from schedule_requests (no timestamp or has request_id)
            const isDraft = !editingAssignment.timestamp || editingAssignment.request_id || editingAssignment.fromScheduleRequest || editingAssignment.isDraft;
            
            if (isDraft) {
                // Delete locally for draft assignments
                setAssignments(prevAssignments => {
                    return prevAssignments.filter(appt => {
                        // Match by key fields to identify the assignment
                        // editingAssignment uses startRaw/endRaw (from event), appt uses start_time/end_time
                        const eventStartTime = editingAssignment.startRaw || editingAssignment.start_time;
                        const eventEndTime = editingAssignment.endRaw || editingAssignment.end_time;
                        const matches = 
                            appt.user_id === editingAssignment.user_id &&
                            appt.center_id === editingAssignment.center_id &&
                            appt.date === editingAssignment.date &&
                            appt.start_time === eventStartTime &&
                            appt.end_time === eventEndTime;
                        return !matches;
                    });
                });
                
                setEditModalOpen(false);
                setEditingAssignment(null);
                setEditForm({
                    user_id: "",
                    center_id: "",
                    room_name: "",
                    date: "",
                    start_time: "",
                    end_time: "",
                    details: "",
                    assignment_type: "assignment"
                });
                setEditRooms([]);
                setEditProviderSearch("");
                setEditCenterSearch("");
                setAssignmentSuccess("Draft assignment deleted successfully!");
                setAssignmentError("");
            } else {
                // Delete via API for real assignments
                await apiClient.delete("/assignments", {
                    user_id: editingAssignment.user_id,
                    timestamp: editingAssignment.timestamp
                });

                if (isDailyView) {
                    await fetchDailyData();
                } else if (isMonthlyView) {
                    await fetchMonthlyData();
                } else {
                    await fetchWeeklyData();
                }

                setEditModalOpen(false);
                setEditingAssignment(null);
                setEditForm({
                    user_id: "",
                    center_id: "",
                    room_name: "",
                    date: "",
                    start_time: "",
                    end_time: "",
                    details: "",
                    assignment_type: "assignment"
                });
                setEditRooms([]);
                setEditProviderSearch("");
                setEditCenterSearch("");
                setAssignmentSuccess("Assignment deleted successfully!");
                setAssignmentError("");
            }
        } catch (e) {
            setEditError("Failed to delete assignment");
            setAssignmentError("Failed to delete assignment");
            setAssignmentSuccess("");
            console.error("Error deleting assignment:", e);
        } finally {
            setEditLoading(false);
        }
    };

    const isManager = user?.activeRole === 'Manager';

    if (loading) {
        return (
            <div className="flex h-screen w-full bg-transparent overflow-hidden">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-lg">Loading dashboard...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen w-full bg-transparent overflow-hidden">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-lg text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    const draftCount = draftReview
        ? assignmentEvents.filter(e => e.request_id === draftReview.request_id).length
        : 0;
    const draftCenterName = draftReview ? (centerMap[draftReview.center_id] || draftReview.center_id) : '';
    const draftReviewReq = draftReview
        ? pendingRequests.find(r => r.request_id === draftReview.request_id)
        : null;

    return (
        <div className="flex flex-col h-screen w-full bg-transparent overflow-hidden">
            {(draftReview || draftReviewMsg) && (
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-300 flex-shrink-0">
                    {draftReview ? (
                        <>
                            <div className="flex items-start justify-between gap-3">
                                <div className="text-sm text-amber-900">
                                    <span className="font-semibold">Draft schedule for review</span>
                                    <span className="ml-2 text-amber-800">
                                        {draftCenterName} · {draftCount} assignments
                                    </span>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button
                                        onClick={handleApproveDraft}
                                        disabled={draftReviewBusy}
                                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm rounded font-medium"
                                    >
                                        {draftReviewBusy ? 'Working…' : 'Approve all'}
                                    </button>
                                    <button
                                        onClick={handleRejectDraft}
                                        disabled={draftReviewBusy}
                                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm rounded font-medium"
                                    >
                                        Reject all
                                    </button>
                                </div>
                            </div>
                            {draftReviewReq && (() => {
                                const req = draftReviewReq;
                                const requestType = (req.request_type || '').replace(/_/g, ' ');
                                const providerLabel = req.best_provider || userMap[req.provider_id]?.name || req.provider_id || '';
                                const requestedBy = userMap[req.user_id]?.name || userMap[req.user_id]?.email || req.user_id || '';
                                const timeRange = [formatTimeStr(req.start_time), formatTimeStr(req.end_time)].filter(Boolean).join('–');
                                const when = req.date || req.month || draftReview.month;
                                const submitted = (req.timestamp || '').slice(0, 10);
                                const reason = req.reasoning || req.summary || req.user_text || '';
                                return (
                                    <div className="mt-2 flex flex-col gap-1.5 text-xs text-amber-900">
                                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                                            {requestType && <span><span className="font-semibold">Type:</span> <span className="capitalize">{requestType}</span></span>}
                                            {when && <span><span className="font-semibold">When:</span> {when}</span>}
                                            {timeRange && <span><span className="font-semibold">Time:</span> {timeRange}</span>}
                                            {req.room && <span><span className="font-semibold">Room:</span> {req.room}</span>}
                                            {providerLabel && <span><span className="font-semibold">Provider:</span> {providerLabel}</span>}
                                            {requestedBy && <span><span className="font-semibold">Requested by:</span> {requestedBy}{submitted ? ` · ${submitted}` : ''}</span>}
                                        </div>
                                        {reason && (
                                            <div className="mt-1 bg-white/70 border border-amber-200 rounded p-2 text-amber-900 whitespace-pre-line leading-relaxed max-h-32 overflow-auto">
                                                {reason}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <span className="text-sm text-amber-900 whitespace-pre-line">{draftReviewMsg}</span>
                    )}
                </div>
            )}
            <div className="flex flex-1 min-h-0 w-full bg-transparent overflow-hidden">
            <div className="w-64 bg-white shadow-lg h-full flex flex-col min-h-0 overflow-hidden">
                <div className="p-4 flex flex-col flex-1 min-h-0 overflow-hidden">
                    <div className="mb-4 flex-shrink-0">
                        <h3 className="font-semibold text-gray-700 mb-2">
                            {isDailyView
                                ? `${dailyViewDate.toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    month: 'long',
                                    day: 'numeric'
                                })} Overview`
                                : "This Week's Overview"
                            }
                        </h3>
                        <div className="space-y-2">
                            <div className="text-sm">
                                <span className="font-medium">Total Assignments:</span> {assignmentEvents.length}
                            </div>
                            {(selectedUsers.size > 0 || selectedCenters.size > 1 || assignmentTypeFilter !== 'all') && (
                                <div className="text-xs text-gray-600">
                                    <span className="font-medium">
                                        {selectedCenters.size > 0 && `${selectedCenters.size} center${selectedCenters.size > 1 ? 's' : ''}`}
                                        {selectedCenters.size > 0 && selectedUsers.size > 0 && ' with '}
                                        {selectedUsers.size > 0 && `${selectedUsers.size} provider${selectedUsers.size > 1 ? 's' : ''}`}
                                        {assignmentTypeFilter !== 'all' && (
                                            <>
                                                {((selectedCenters.size > 0 || selectedUsers.size > 0) ? ' showing ' : 'Showing ')}
                                                {assignmentTypeFilter === 'assignments' ? 'assignments only' : assignmentTypeFilter === 'shifts' ? 'shifts only' : 'drafts only'}
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}
                            <div className="flex gap-2">
                                {(selectedUsers.size > 0 || selectedCenters.size > 1 || assignmentTypeFilter !== 'all') && (
                                    <button
                                        onClick={clearAllFilters}
                                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 transition-colors"
                                    >
                                        Remove all filters
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsMultipleSelectMode(!isMultipleSelectMode)}
                                    className={`px-3 py-1 text-xs rounded border transition-colors ${
                                        isMultipleSelectMode
                                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
                                    }`}
                                >
                                    {isMultipleSelectMode ? 'Single Select' : 'Select Multiple'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="font-semibold text-gray-700">
                                        Pending Requests {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                                    </h3>
                                    <button
                                        onClick={fetchPendingRequests}
                                        disabled={pendingRequestsLoading}
                                        className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                                        title="Refresh"
                                    >
                                        {pendingRequestsLoading ? '…' : '↻'}
                                    </button>
                                </div>
                                {pendingRequests.length === 0 ? (
                                    <div className="text-xs text-gray-500 italic px-1">
                                        {pendingRequestsLoading ? 'Loading…' : 'No pending requests'}
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {pendingRequests.map(req => {
                                            const isActive = draftReview && draftReview.request_id === req.request_id;
                                            const centerLabel = centerMap[req.center_id] || req.center_name || req.center_id;
                                            const providerLabel = req.best_provider || userMap[req.provider_id]?.name || req.provider_id || '';
                                            const userLabel = userMap[req.user_id]?.name || userMap[req.user_id]?.email || req.user_id || '';
                                            const timeRange = [formatTimeStr(req.start_time), formatTimeStr(req.end_time)].filter(Boolean).join('–');
                                            const requestType = (req.request_type || '').replace(/_/g, ' ');
                                            const detail = req.summary || req.user_text || req.reasoning || '';
                                            return (
                                                <button
                                                    key={req.request_id}
                                                    onClick={() => openDraftReview(req)}
                                                    className={`w-full text-left px-2 py-1.5 text-xs rounded border transition-colors ${
                                                        isActive
                                                            ? 'bg-amber-100 border-amber-400 text-amber-900'
                                                            : 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100'
                                                    }`}
                                                    title={req.reasoning || req.user_text || req.request_id}
                                                >
                                                    <div className="flex items-center justify-between gap-1">
                                                        <span className="font-medium truncate">{centerLabel}</span>
                                                        {requestType && (
                                                            <span className="shrink-0 text-[9px] uppercase tracking-wide bg-amber-200 text-amber-800 rounded px-1 py-0.5">
                                                                {requestType}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {providerLabel && (
                                                        <div className="text-[11px] font-medium text-amber-900 truncate">
                                                            {providerLabel}
                                                        </div>
                                                    )}
                                                    <div className="text-[11px] text-amber-700 truncate">
                                                        {[req.date || req.month, timeRange, req.room].filter(Boolean).join(' · ')}
                                                    </div>
                                                    {userLabel && (
                                                        <div className="text-[10px] text-amber-600 truncate">
                                                            by {userLabel}{req.timestamp ? ` · ${(req.timestamp || '').slice(0, 10)}` : ''}
                                                        </div>
                                                    )}
                                                    {detail && (
                                                        <div className="text-[10px] text-amber-700 mt-0.5 line-clamp-2">
                                                            {detail}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">
                                    Users {selectedUsers.size > 0 && `(${selectedUsers.size} selected)`}
                                </h3>
                                <div className="space-y-2">
                                    {(() => {
                                        const users = Object.entries(userMap).map(([userId, userData]) => ({
                                            userId,
                                            ...userData,
                                            user_name: userData.name
                                        }));
                                        const groupedUsers = groupProvidersByDegree(users);
                                        const degreeOrder = ['MD', 'DO', 'CRNA', 'NP', 'PA', 'RN', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA', 'Other'];
                                        
                                        return degreeOrder.filter(degree => groupedUsers[degree]).map(degree => (
                                            <div key={degree} className="space-y-1">
                                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                    {degree}
                                                </div>
                                                {groupedUsers[degree].map(({ userId, name }) => {
                                                    const { nameWithoutDegree, degree: userDegree } = extractDegreeFromName(name);
                                                    return (
                                                        <button
                                                            key={userId}
                                                            onClick={() => handleUserSelect(userId)}
                                                            className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${selectedUsers.has(userId)
                                                                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                                : 'text-gray-600 hover:bg-gray-100'
                                                                }`}
                                                        >
                                                            {nameWithoutDegree}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">
                                    Facilities {selectedCenters.size > 0 && `(${selectedCenters.size} selected)`}
                                </h3>
                                <div className="space-y-1">
                                    {(() => {
                                        const sortedCenters = centers.sort((a, b) => {
                                            const aType = a.type || 'medical_center';
                                            const bType = b.type || 'medical_center';

                                            if (aType === 'hospital' && bType !== 'hospital') return -1;
                                            if (bType === 'hospital' && aType !== 'hospital') return 1;

                                            return (a.name || '').localeCompare(b.name || '');
                                        });

                                        const hospitals = sortedCenters.filter(c => c.type === 'hospital');
                                        const otherCenters = sortedCenters.filter(c => c.type && c.type !== 'hospital');
                                        const noTypeCenters = sortedCenters.filter(c => !c.type);

                                        return (
                                            <>
                                                {hospitals.length > 0 && (
                                                    <>
                                                        <div className="text-xs text-gray-500 font-medium px-2 py-1">Hospitals</div>
                                                        {hospitals.map(center => (
                                                            <button
                                                                key={center.center_id}
                                                                onClick={() => handleCenterSelect(center.center_id)}
                                                                className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${selectedCenters.has(center.center_id)
                                                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                {center.name}
                                                            </button>
                                                        ))}
                                                    </>
                                                )}
                                                {otherCenters.length > 0 && (
                                                    <>
                                                        <div className="text-xs text-gray-500 font-medium px-2 py-1">Centers</div>
                                                        {otherCenters.map(center => (
                                                            <button
                                                                key={center.center_id}
                                                                onClick={() => handleCenterSelect(center.center_id)}
                                                                className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${selectedCenters.has(center.center_id)
                                                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                {center.name}
                                                            </button>
                                                        ))}
                                                    </>
                                                )}
                                                {noTypeCenters.length > 0 && (
                                                    <>
                                                        <div className="text-xs text-gray-500 font-medium px-2 py-1">Others</div>
                                                        {noTypeCenters.map(center => (
                                                            <button
                                                                key={center.center_id}
                                                                onClick={() => handleCenterSelect(center.center_id)}
                                                                className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${selectedCenters.has(center.center_id)
                                                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                {center.name}
                                                            </button>
                                                        ))}
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">Assignment Type</h3>
                                <div className="space-y-1">
                                    <button
                                        onClick={() => setAssignmentTypeFilter('all')}
                                        className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${assignmentTypeFilter === 'all'
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        All Assignments
                                    </button>
                                    <button
                                        onClick={() => setAssignmentTypeFilter('assignments')}
                                        className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${assignmentTypeFilter === 'assignments'
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        Assignments Only
                                    </button>
                                    <button
                                        onClick={() => setAssignmentTypeFilter('shifts')}
                                        className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${assignmentTypeFilter === 'shifts'
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        Shifts Only
                                    </button>
                                    <button
                                        onClick={() => setAssignmentTypeFilter('drafts')}
                                        className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${assignmentTypeFilter === 'drafts'
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        Drafts Only
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            {!isDailyView && !isMonthlyView && (
                <div className="flex-1 flex flex-col h-full min-h-0 w-full overflow-hidden">
                    {assignmentSuccess && (
                        <div className="mx-4 mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm flex items-center justify-between">
                            <span>{assignmentSuccess}</span>
                            <button
                                onClick={() => setAssignmentSuccess("")}
                                className="ml-2 text-green-600 hover:text-green-800"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                    {assignmentError && (
                        <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm flex items-center justify-between">
                            <span>{assignmentError}</span>
                            <button
                                onClick={() => setAssignmentError("")}
                                className="ml-2 text-red-600 hover:text-red-800"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center justify-between h-14 px-4 bg-white border-b shadow-sm sticky top-0 z-20">
                        <div className="flex items-center gap-4">
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setSelectedDate(addDays(selectedDate, -7))}
                                aria-label="Previous week"
                            >
                                <ChevronLeftIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <div className="flex flex-col items-center">
                                <span className="font-semibold text-lg">{weekRange}</span>
                                {(() => {
                                    const currentWeekSunday = getSunday(today);
                                    const selectedWeekSunday = getSunday(selectedDate);
                                    const isCurrentWeek = currentWeekSunday.toDateString() === selectedWeekSunday.toDateString();
                                    return !isCurrentWeek ? (
                                        <button
                                            onClick={() => goToCurrentWeek(setSelectedDate)}
                                            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                            title="Go back to current week"
                                        >
                                            return to current week
                                        </button>
                                    ) : null;
                                })()}
                            </div>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setSelectedDate(addDays(selectedDate, 7))}
                                aria-label="Next week"
                            >
                                <ChevronRightIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => {
                                    setSelectedDateForPicker(today);
                                    setDatePickerModalOpen(true);
                                }}
                                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-green-200 text-green-700 hover:bg-green-300 flex items-center gap-2"
                                title="Go to specific date"
                            >
                                <CalendarIcon className="h-4 w-4" />
                                Go to date
                            </button>
                            <button
                                onClick={() => {
                                    if (!isDailyView) {
                                        setDailyViewDate(today);
                                    }
                                    setIsDailyView(!isDailyView);
                                    setIsMonthlyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isDailyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Daily View
                            </button>
                            <button
                                onClick={() => {
                                    setIsDailyView(false);
                                    setIsMonthlyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${!isDailyView && !isMonthlyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Weekly View
                            </button>
                            <button
                                onClick={() => {
                                    if (!isMonthlyView) {
                                        setMonthlyViewDate(today);
                                    }
                                    setIsMonthlyView(!isMonthlyView);
                                    setIsDailyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isMonthlyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Monthly View
                            </button>
                            <button
                                onClick={() => handlePrintCalendar()}
                                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-2"
                                title="Print Calendar"
                            >
                                <PrinterIcon className="h-4 w-4" />
                                Print
                            </button>
                        </div>
                    </div>
                    <div className="flex w-full bg-white border-b" style={{ height: 48 }}>
                        <div className="w-16" />
                        {weekDates.map((date, idx) => (
                            <div
                                key={idx}
                                className="flex-1 flex flex-col items-center justify-center border-r last:border-r-0"
                                style={{ minWidth: 0 }}
                            >
                                <span className="font-semibold text-base text-gray-700">{days[idx]}</span>
                                <span className="text-sm text-gray-500">{date.getDate()}</span>
                            </div>
                        ))}
                    </div>
                    <div className="relative flex-1 w-full bg-white overflow-y-auto" style={{ minHeight: 0 }}>
                        <div className="absolute left-0 top-0 w-16" style={{ height: numRows * rowHeight }}>
                            {Array.from({ length: dayEndHour - dayStartHour }, (_, i) => {
                                const hour = dayStartHour + i;
                                const top = i * rowHeight;
                                return (
                                    <div
                                        key={hour}
                                        className="absolute text-xs text-gray-500"
                                        style={{ top, height: rowHeight, lineHeight: `${rowHeight}px` }}
                                    >
                                        {hour % 12 === 0 ? 12 : hour % 12}:00 {hour < 12 ? 'AM' : 'PM'}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="absolute left-16 top-0 right-0" style={{ height: numRows * rowHeight }}>
                            {weekDates.map((_, idx) => (
                                <div
                                    key={idx}
                                    className="absolute top-0 bottom-0 border-r border-gray-200"
                                    style={{ left: `calc(${idx} / ${numDays} * 100%)`, width: 1, zIndex: 1 }}
                                />
                            ))}
                            {Array.from({ length: numRows + 1 }, (_, i) => {
                                const top = i * rowHeight;
                                return (
                                    <div
                                        key={i}
                                        className="absolute left-0 right-0 border-b border-gray-200"
                                        style={{ top, height: 0, zIndex: 1 }}
                                    />
                                );
                            })}
                            {weekDates.map((date, dayIdx) => {
                                const dayEvents = allEvents.filter((e) => e.date === toISODate(date));
                                const availUnavailEvents = dayEvents.filter(e => e.isAvailable || e.isUnavailable);
                                const otherEvents = dayEvents.filter(e => !e.isAvailable && !e.isUnavailable);
                                return (
                                    <div
                                        key={dayIdx}
                                        className="absolute top-0 h-full"
                                        style={{
                                            left: `calc(${dayIdx} / ${numDays} * 100%)`,
                                            width: `calc(100% / ${numDays})`,
                                            zIndex: 2,
                                        }}
                                    >
                                        <div className="relative w-full h-full">
                                            {availUnavailEvents.map((event, i) => {
                                                const eventStartMinutes = (event.start - dayStartHour) * 60;
                                                const eventEndMinutes = (event.end - dayStartHour) * 60;
                                                const top = eventStartMinutes * (rowHeight / 60);
                                                const height = (eventEndMinutes - eventStartMinutes) * (rowHeight / 60) - 8;
                                                const colorMap = COLOR_CLASSES;
                                                let colorKey = 'gray';
                                                if (event.color && event.color.includes('blue')) colorKey = 'blue';
                                                else if (event.color && event.color.includes('green')) colorKey = 'green';
                                                else if (event.color && event.color.includes('pink')) colorKey = 'pink';
                                                else if (event.color && event.color.includes('yellow')) colorKey = 'yellow';
                                                else if (event.color && event.color.includes('red')) colorKey = 'red';
                                                const palette = colorMap[colorKey];
                                                return (
                                                    <div
                                                        key={i}
                                                        className={`absolute px-3 py-2 flex flex-col items-start font-semibold text-sm border-l-4 ${palette.border} ${palette.bg} ${palette.text} overflow-hidden whitespace-pre-line rounded shadow-sm backdrop-blur-sm`}
                                                        style={{
                                                            left: 0,
                                                            right: 0,
                                                            top,
                                                            height,
                                                            maxWidth: '100%',
                                                            zIndex: 5,
                                                            color: event.isUnavailable ? '#b91c1c' : undefined,
                                                            margin: '3px',
                                                            backgroundClip: 'padding-box',
                                                        }}
                                                    >
                                                        {event.title}
                                                        <span
                                                            className={`text-xs font-normal mt-1 ${event.isUnavailable ? 'text-red-300' : event.isAvailable ? 'text-green-400' : 'text-gray-400'}`}
                                                            style={{ fontWeight: 400 }}
                                                        >
                                                            {formatTimeStr(event.startRaw || event.startStr || event.startStrRaw || event.startStrValue || event.startStrTime || event.startStr || (event.start < 10 ? `0${event.start}:00` : `${Math.floor(event.start)}:${(event.start % 1 === 0.5 ? '30' : '00')}`))}
                                                            {' - '}
                                                            {formatTimeStr(event.endRaw || event.endStr || event.endStrRaw || event.endStrValue || event.endStrTime || event.endStr || (event.end < 10 ? `0${event.end}:00` : `${Math.floor(event.end)}:${(event.end % 1 === 0.5 ? '30' : '00')}`))}
                                                        </span>
                                                        {(selectedUsers.size === 0 || selectedUsers.size > 1) && event.userEmail && (
                                                            <span className="text-xs font-normal text-gray-400 mt-1" style={{ fontWeight: 400 }}>
                                                                {event.userEmail}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {otherEvents.map((event, i) => {
                                                const eventStartMinutes = (event.start - dayStartHour) * 60;
                                                const eventEndMinutes = (event.end - dayStartHour) * 60;
                                                const top = eventStartMinutes * (rowHeight / 60);
                                                const height = (eventEndMinutes - eventStartMinutes) * (rowHeight / 60) - 8;

                                                const eventColor = getEventColor(event);
                                                const colorMap = COLOR_CLASSES;
                                                const palette = colorMap[eventColor] || colorMap.gray;

                                                const overlapsWithAvail = availUnavailEvents.some(av =>
                                                    event.start < av.end && event.end > av.start
                                                );

                                                const position = calculateEventPosition(event, i, otherEvents);
                                                const isHovered = hoveredEvent === `${dayIdx}-${i}`;

                                                return (
                                                    <div
                                                        key={i}
                                                        className={`absolute px-3 py-2 flex flex-col items-start font-semibold text-sm border-l-4 ${palette.border} ${palette.bg} ${palette.text} overflow-hidden whitespace-pre-line rounded shadow-sm backdrop-blur-sm cursor-pointer hover:shadow-lg`}
                                                        onClick={() => handleEventClick(event)}
                                                        style={{
                                                            left: overlapsWithAvail ? 8 : (position.isConflicting ? position.left : 0),
                                                            right: overlapsWithAvail ? 8 : (position.isConflicting ? 'auto' : 0),
                                                            width: overlapsWithAvail ? 'calc(100% - 16px)' : (position.isConflicting ? position.width : '100%'),
                                                            top,
                                                            height: Math.max(height, 20), 
                                                            zIndex: isHovered ? 100 : 10,
                                                            margin: position.isConflicting ? '1px' : '3px',
                                                            backgroundClip: 'padding-box',
                                                        }}
                                                        onMouseEnter={() => setHoveredEvent(`${dayIdx}-${i}`)}
                                                        onMouseLeave={() => setHoveredEvent(null)}
                                                    >
                                                        {event.centerName ? (
                                                            <>
                                                                <span className="text-xs font-semibold">
                                                                    {event.centerName}
                                                                </span>
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {event.room_name && (
                                                                        <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 bg-white text-purple-900 border border-purple-200 text-[10px] rounded-full uppercase tracking-wide font-semibold leading-none">
                                                                            {event.room_name}
                                                                        </span>
                                                                    )}
                                                                    {event.assignment_type === 'shift' && (
                                                                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-[10px] rounded uppercase tracking-wide font-semibold">
                                                                            {getShiftType(event.startRaw, event.endRaw)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {event.details && (
                                                                    <span className="text-xs font-normal text-gray-500 mt-1" style={{ fontWeight: 400 }}><b>Details:</b> {event.details}</span>
                                                                )}
                                                                {(selectedUsers.size === 0 || selectedUsers.size > 1) && (
                                                                    <span className="text-xs font-normal text-gray-500 mt-1" style={{ fontWeight: 400 }}><b>Provider:</b> {event.userEmail}</span>
                                                                )}

                                                            </>
                                                        ) : (
                                                            event.title
                                                        )}
                                                        <span className="text-xs font-normal mt-1 text-gray-400" style={{ fontWeight: 400 }}>
                                                            {event.isOvernight ? (
                                                                <>
                                                                    {formatTimeStr(event.startRaw)} - {formatTimeStr(event.endRaw)}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {formatTimeStr(event.startRaw || event.startStr || event.startStrRaw || event.startStrValue || event.startStrTime || event.startStr || (event.start < 10 ? `0${event.start}:00` : `${Math.floor(event.start)}:${(event.start % 1 === 0.5 ? '30' : '00')}`))}
                                                                    {' - '}
                                                                    {formatTimeStr(event.endRaw || event.endStr || event.endStrRaw || event.endStrValue || event.endStrTime || event.endStr || (event.end < 10 ? `0${event.end}:00` : `${Math.floor(event.end)}:${(event.end % 1 === 0.5 ? '30' : '00')}`))}
                                                                </>
                                                            )}
                                                        </span>
                                                        {event.assignment_type === 'draft' && (
                                                            <div className="mt-1">
                                                                <span className="px-1 py-0.5 bg-amber-100 text-amber-800 text-xs rounded font-medium">
                                                                    DRAFT
                                                                </span>
                                                            </div>
                                                        )}
                                                        {event.isOvernight && (
                                                            <span className="mt-1 px-1 py-0.5 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                                                                {event.isOvernightPart === 'first' ? 'OVERNIGHT' : 'OVERNIGHT'}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {isDailyView && (
                <div className="flex-1 flex flex-col h-full min-h-0 w-full overflow-hidden">
                    {assignmentSuccess && (
                        <div className="mx-4 mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm flex items-center justify-between">
                            <span>{assignmentSuccess}</span>
                            <button
                                onClick={() => setAssignmentSuccess("")}
                                className="ml-2 text-green-600 hover:text-green-800"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                    {assignmentError && (
                        <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm flex items-center justify-between">
                            <span>{assignmentError}</span>
                            <button
                                onClick={() => setAssignmentError("")}
                                className="ml-2 text-red-600 hover:text-red-800"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center justify-between h-14 px-4 bg-white border-b shadow-sm sticky top-0 z-20">
                        <div className="flex items-center gap-4">
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setDailyViewDate(addDays(dailyViewDate, -1))}
                                aria-label="Previous day"
                            >
                                <ChevronLeftIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <div className="flex flex-col items-center">
                                <span className="font-semibold text-lg">
                                    {dailyViewDate.toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </span>
                                {(() => {
                                    const currentDay = new Date();
                                    const isCurrentDay = currentDay.toDateString() === dailyViewDate.toDateString();
                                    return !isCurrentDay ? (
                                        <button
                                            onClick={() => goToCurrentDay(setDailyViewDate)}
                                            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                            title="Go back to current day"
                                        >
                                            return to today
                                        </button>
                                    ) : null;
                                })()}
                            </div>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => setDailyViewDate(addDays(dailyViewDate, 1))}
                                aria-label="Next day"
                            >
                                <ChevronRightIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => {
                                    setSelectedDateForPicker(today);
                                    setDatePickerModalOpen(true);
                                }}
                                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-green-200 text-green-700 hover:bg-green-300 flex items-center gap-2"
                                title="Go to specific date"
                            >
                                <CalendarIcon className="h-4 w-4" />
                                Go to date
                            </button>
                            <button
                                onClick={() => {
                                    if (!isDailyView) {
                                        setDailyViewDate(today);
                                    }
                                    setIsDailyView(!isDailyView);
                                    setIsMonthlyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isDailyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Daily View
                            </button>
                            <button
                                onClick={() => {
                                    setIsDailyView(false);
                                    setIsMonthlyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${!isDailyView && !isMonthlyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Weekly View
                            </button>
                            
                            <button
                                onClick={() => {
                                    if (!isMonthlyView) {
                                        setMonthlyViewDate(today);
                                    }
                                    setIsMonthlyView(!isMonthlyView);
                                    setIsDailyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isMonthlyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Monthly View
                            </button>
                            <button
                                onClick={() => handlePrintCalendar()}
                                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-2"
                                title="Print Calendar"
                            >
                                <PrinterIcon className="h-4 w-4" />
                                Print
                            </button>
                        </div>
                    </div>

                    <div className="flex w-full bg-white border-b" style={{ height: 48 }}>
                        <div className="w-48 flex items-center justify-center border-r">
                            <span className="font-semibold text-base text-gray-700">Providers</span>
                        </div>
                        {Array.from({ length: dayEndHour - dayStartHour }, (_, i) => {
                            const hour = dayStartHour + i;
                            return (
                                <div
                                    key={hour}
                                    className="flex-1 flex flex-col items-center justify-center border-r last:border-r-0"
                                    style={{ minWidth: 0 }}
                                >
                                    <span className="font-medium text-xs text-gray-600">
                                        {hour % 12 === 0 ? 12 : hour % 12}:00
                                    </span>
                                    <span className="font-normal text-xs text-gray-500">
                                        {hour < 12 ? 'AM' : 'PM'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="relative flex-1 w-full bg-white overflow-y-auto" style={{ minHeight: 0 }}>
                        <div className="absolute left-0 top-0 w-48" style={{ height: Object.keys(userMap).length * 60 }}>
                            {Object.entries(userMap).map(([userId, userData], idx) => (
                                <div
                                    key={userId}
                                    className="absolute text-sm text-gray-700 border-b border-gray-200"
                                    style={{ top: idx * 60, height: 60, lineHeight: '60px', paddingLeft: '16px' }}
                                >
                                    {userData.name}
                                </div>
                            ))}
                        </div>

                        <div className="absolute left-48 top-0 right-0" style={{ height: Object.keys(userMap).length * 60 }}>
                            {Array.from({ length: dayEndHour - dayStartHour }, (_, i) => (
                                <div
                                    key={i}
                                    className="absolute top-0 bottom-0 border-r border-gray-200"
                                    style={{ left: `calc(${i} / ${dayEndHour - dayStartHour} * 100%)`, width: 1, zIndex: 1 }}
                                />
                            ))}

                            {Object.keys(userMap).map((_, idx) => (
                                <div
                                    key={idx}
                                    className="absolute left-0 right-0 border-b border-gray-200"
                                    style={{ top: idx * 60, height: 0, zIndex: 1 }}
                                />
                            ))}

                            {Object.entries(userMap).map(([userId, userData], userIdx) => {
                                const userAssignments = assignmentEvents.filter(appt => appt.user_id === userId);
                                const userAvail = availability[userId] || [];
                                const userUnavail = unavailability[userId] || [];
                                const dailyDateStr = toISODate(dailyViewDate);

                                const userAvailEvents = userAvail.flatMap(a => {
                                    const orig = new Date(a.date);
                                    const dailyDate = new Date(dailyDateStr);

                                    if (
                                        (a.repeat === "none" && localDateString(dailyDate) === a.date) ||
                                        (a.repeat === "weekly" && dailyDate.getDay() === orig.getDay() && localDateString(dailyDate) >= localDateString(orig)) ||
                                        (a.repeat === "odd" && dailyDate.getDay() === orig.getDay() && localDateString(dailyDate) >= localDateString(orig) && isOddWeek) ||
                                        (a.repeat === "even" && dailyDate.getDay() === orig.getDay() && localDateString(dailyDate) >= localDateString(orig) && isEvenWeek)
                                    ) {
                                        return [{
                                            date: dailyDateStr,
                                            start: a.allDay ? 9 : timeToDecimal(a.start),
                                            end: a.allDay ? 17 : timeToDecimal(a.end),
                                            title: "Available",
                                            color: "border-green-400 bg-green-100",
                                            text: "text-green-700",
                                            isAvailable: true,
                                            originalEvent: a,
                                            startRaw: a.start,
                                            endRaw: a.end,
                                            repeat: a.repeat,
                                            allDay: a.allDay,
                                            user_id: userId,
                                            userEmail: userData.name,
                                        }];
                                    }
                                    return [];
                                });

                                const userUnavailEvents = userUnavail.flatMap(u => {
                                    const orig = new Date(u.date);
                                    const dailyDate = new Date(dailyDateStr);

                                    if (
                                        (u.repeat === "none" && localDateString(dailyDate) === u.date) ||
                                        (u.repeat === "weekly" && dailyDate.getDay() === orig.getDay() && localDateString(dailyDate) >= localDateString(orig)) ||
                                        (u.repeat === "odd" && dailyDate.getDay() === orig.getDay() && localDateString(dailyDate) >= localDateString(orig) && isOddWeek) ||
                                        (u.repeat === "even" && dailyDate.getDay() === orig.getDay() && localDateString(dailyDate) >= localDateString(orig) && isEvenWeek)
                                    ) {
                                        return [{
                                            date: dailyDateStr,
                                            start: u.allDay ? 9 : timeToDecimal(u.start),
                                            end: u.allDay ? 17 : timeToDecimal(u.end),
                                            title: "Not available",
                                            color: "border-red-300 bg-red-100",
                                            text: "text-red-700",
                                            isUnavailable: true,
                                            originalEvent: u,
                                            startRaw: u.start,
                                            endRaw: u.end,
                                            repeat: u.repeat,
                                            allDay: u.allDay,
                                            user_id: userId,
                                            userEmail: userData.name,
                                        }];
                                    }
                                    return [];
                                });

                                const allUserEvents = [...userAvailEvents, ...userUnavailEvents];

                                return [
                                    ...allUserEvents.map((event, eventIdx) => {
                                        const startHour = Math.max(dayStartHour, Math.floor(event.start));
                                        const endHour = Math.min(dayEndHour, Math.ceil(event.end));
                                        const startCol = startHour - dayStartHour;
                                        const endCol = endHour - dayStartHour;
                                        const colSpan = endCol - startCol;

                                        const left = `calc(${startCol} / ${dayEndHour - dayStartHour} * 100% + 4px)`;
                                        const width = `calc(${colSpan} / ${dayEndHour - dayStartHour} * 100% - 8px)`;
                                        const top = userIdx * 60 + 4;

                                        const colorMap = COLOR_CLASSES;
                                        let colorKey = 'gray';
                                        if (event.color && event.color.includes('blue')) colorKey = 'blue';
                                        else if (event.color && event.color.includes('green')) colorKey = 'green';
                                        else if (event.color && event.color.includes('pink')) colorKey = 'pink';
                                        else if (event.color && event.color.includes('yellow')) colorKey = 'yellow';
                                        else if (event.color && event.color.includes('red')) colorKey = 'red';
                                        const palette = colorMap[colorKey];

                                        return (
                                            <div
                                                key={`${userId}-avail-${eventIdx}`}
                                                className={`absolute px-2 py-1 flex flex-col items-start font-semibold text-xs border-l-4 ${palette.border} ${palette.bg} ${palette.text} overflow-hidden whitespace-pre-line rounded shadow-sm backdrop-blur-sm`}
                                                style={{
                                                    left,
                                                    width,
                                                    top,
                                                    height: 52,
                                                    zIndex: 5,
                                                    backgroundClip: 'padding-box',
                                                    color: event.isUnavailable ? '#b91c1c' : undefined,
                                                }}
                                            >
                                                <span className="font-medium">{event.title}</span>
                                                <span className="text-xs font-normal text-gray-500 mt-1">
                                                    {formatTimeStr(event.startRaw)} - {formatTimeStr(event.endRaw)}
                                                </span>
                                            </div>
                                        );
                                    }),
                                    ...userAssignments.map((appt, apptIdx) => {
                                        const apptDate = new Date(appt.date);
                                        const dailyDateStr = toISODate(dailyViewDate);

                                        if (appt.date !== dailyDateStr && !(appt.isOvernight && appt.isOvernightPart === 'second')) return null;

                                        let start = 9, end = 10;
                                        if (appt.startRaw && appt.endRaw) {
                                            const [sh, sm] = appt.startRaw.split(":").map(Number);
                                            const [eh, em] = appt.endRaw.split(":").map(Number);
                                            start = sh + (sm || 0) / 60;
                                            end = eh + (em || 0) / 60;
                                        }

                                        if (appt.isOvernight && appt.isOvernightPart === 'second') {
                                            start = 0;
                                        }

                                        const startHour = Math.max(dayStartHour, Math.floor(start));
                                        const endHour = Math.min(dayEndHour, Math.ceil(end));
                                        const startCol = startHour - dayStartHour;
                                        const endCol = endHour - dayStartHour;
                                        const colSpan = endCol - startCol;

                                        const left = `calc(${startCol} / ${dayEndHour - dayStartHour} * 100% + 4px)`;
                                        const width = `calc(${colSpan} / ${dayEndHour - dayStartHour} * 100% - 8px)`;
                                        const top = userIdx * 60 + 4;

                                        const eventColor = getEventColor(appt);
                                        const colorMap = COLOR_CLASSES;
                                        const palette = colorMap[eventColor] || colorMap.gray;

                                        const overlapsWithAvail = allUserEvents.some(av =>
                                            start < av.end && end > av.start
                                        );

                                        const userAssignmentsForDay = userAssignments.filter(a => a.date === dailyDateStr || (a.isOvernight && a.isOvernightPart === 'second')).map(a => {
                                            let aStart = 9, aEnd = 10;
                                            if (a.startRaw && a.endRaw) {
                                                const [sh, sm] = a.startRaw.split(":").map(Number);
                                                const [eh, em] = a.endRaw.split(":").map(Number);
                                                aStart = sh + (sm || 0) / 60;
                                                aEnd = eh + (em || 0) / 60;
                                            }
                                            
                                            if (a.isOvernight && a.isOvernightPart === 'second') {
                                                aStart = 0;
                                            }
                                            
                                            return {
                                                ...a,
                                                start: aStart,
                                                end: aEnd
                                            };
                                        });

                                        const eventForPosition = {
                                            ...appt,
                                            start: start,
                                            end: end
                                        };
                                        const position = calculateEventPosition(eventForPosition, apptIdx, userAssignmentsForDay);
                                        const isHovered = hoveredEvent === `${userId}-${apptIdx}`;

                                        return (
                                            <div
                                                key={`${userId}-${apptIdx}`}
                                                className={`absolute px-2 py-1 flex flex-col items-start font-semibold text-xs border-l-4 ${palette.border} ${palette.bg} ${palette.text} overflow-hidden whitespace-pre-line rounded shadow-sm backdrop-blur-sm cursor-pointer hover:shadow-lg`}
                                                onClick={() => handleEventClick(appt)}
                                                style={{
                                                    left: overlapsWithAvail ? `calc(${left} + 8px)` : (position.isConflicting ? position.left : left),
                                                    width: overlapsWithAvail ? `calc(${width} - 16px)` : (position.isConflicting ? position.width : width),
                                                    top: position.isConflicting ? (userIdx * 60 + 4 + (position.positionInCollision * (52 / position.totalConflicting))) : top,
                                                    height: position.isConflicting ? (52 / position.totalConflicting - 2) : 52,
                                                    zIndex: isHovered ? 100 : 10,
                                                    backgroundClip: 'padding-box',
                                                }}
                                                onMouseEnter={() => setHoveredEvent(`${userId}-${apptIdx}`)}
                                                onMouseLeave={() => setHoveredEvent(null)}
                                            >
                                                <span className="font-medium">
                                                    {appt.centerName}
                                                    {appt.assignment_type === 'shift' && (
                                                        <div className="mt-1">
                                                            <span className="px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                                                                {getShiftType(appt.startRaw, appt.endRaw)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {appt.assignment_type === 'draft' && (
                                                        <div className="mt-1">
                                                            <span className="px-1 py-0.5 bg-amber-100 text-amber-800 text-xs rounded font-medium">
                                                                DRAFT
                                                            </span>
                                                        </div>
                                                    )}
                                                    {appt.isOvernight && (
                                                        <div className="mt-1">
                                                            <span className="px-1 py-0.5 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                                                                {appt.isOvernightPart === 'first' ? 'OVERNIGHT' : 'OVERNIGHT (CONTINUED)'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </span>
                                                {appt.room_name && (
                                                    <span className="inline-flex items-center gap-1 text-xs font-normal text-gray-600">
                                                        Room
                                                        <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold leading-none">{appt.room_name}</span>
                                                    </span>
                                                )}
                                                {appt.details && (
                                                    <span className="text-xs font-normal text-gray-500"><b>Details:</b> {appt.details}</span>
                                                )}
                                                <span className="text-xs font-normal text-gray-500 mt-1">
                                                    {formatTimeStr(appt.startRaw)} - {formatTimeStr(appt.endRaw)}
                                                </span>
                                            </div>
                                        );
                                    })
                                ];
                            })}
                        </div>
                    </div>
                </div>
            )}

            {isMonthlyView && (
                <div className="flex-1 flex flex-col h-full min-h-0 w-full overflow-hidden">
                    {assignmentSuccess && (
                        <div className="mx-4 mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm flex items-center justify-between">
                            <span>{assignmentSuccess}</span>
                            <button
                                onClick={() => setAssignmentSuccess("")}
                                className="ml-2 text-green-600 hover:text-green-800"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                    {assignmentError && (
                        <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm flex items-center justify-between">
                            <span>{assignmentError}</span>
                            <button
                                onClick={() => setAssignmentError("")}
                                className="ml-2 text-red-600 hover:text-red-800"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center justify-between h-14 px-4 bg-white border-b shadow-sm sticky top-0 z-20">
                        <div className="flex items-center gap-4">
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => goToPreviousMonth(setMonthlyViewDate)}
                                aria-label="Previous month"
                            >
                                <ChevronLeftIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <div className="flex flex-col items-center">
                                <span className="font-semibold text-lg">
                                    {monthlyViewDate.toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </span>
                                {monthlyViewDate.getMonth() !== today.getMonth() || monthlyViewDate.getFullYear() !== today.getFullYear() ? (
                                    <button
                                        onClick={() => goToToday(setMonthlyViewDate)}
                                        className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                        title="Go back to current month"
                                    >
                                        return to current month
                                    </button>
                                ) : null}
                            </div>
                            <button
                                className="p-2 rounded hover:bg-gray-100"
                                onClick={() => goToNextMonth(setMonthlyViewDate)}
                                aria-label="Next month"
                            >
                                <ChevronRightIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => {
                                    setSelectedDateForPicker(today);
                                    setDatePickerModalOpen(true);
                                }}
                                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-green-200 text-green-700 hover:bg-green-300 flex items-center gap-2"
                                title="Go to specific date"
                            >
                                <CalendarIcon className="h-4 w-4" />
                                Go to date
                            </button>
                            <button
                                onClick={() => {
                                    if (!isDailyView) {
                                        setDailyViewDate(today);
                                    }
                                    setIsDailyView(!isDailyView);
                                    setIsMonthlyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isDailyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Daily View
                            </button>
                            <button
                                onClick={() => {
                                    setIsDailyView(false);
                                    setIsMonthlyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${!isDailyView && !isMonthlyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                Weekly View
                            </button>
                            <button
                                onClick={() => {
                                    if (!isMonthlyView) {
                                        setMonthlyViewDate(today);
                                    }
                                    setIsMonthlyView(!isMonthlyView);
                                    setIsDailyView(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isMonthlyView
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                                            >
                                Monthly View
                            </button>
                            <button
                                onClick={() => handlePrintCalendar()}
                                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-2"
                                title="Print Calendar"
                            >
                                <PrinterIcon className="h-4 w-4" />
                                Print
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 bg-white overflow-y-auto">
                        <div className="p-4">
                            <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-t-lg">
                                {days.map(day => (
                                    <div key={day} className="bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 text-center">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 border-t-0 rounded-b-lg" style={{ gridAutoRows: 'minmax(120px, auto)' }}>
                                {getCalendarDays(monthlyViewDate).map((date, index) => {
                                    const dayAssignments = getAssignmentsForDate(date, assignmentEvents);
                                    const isCurrentMonthDay = isCurrentMonth(date, monthlyViewDate);
                                    const isTodayDate = isToday(date);

                                    const assignmentsByCenter = {};
                                    dayAssignments.forEach(appt => {
                                        if (!assignmentsByCenter[appt.center_id]) {
                                            assignmentsByCenter[appt.center_id] = [];
                                        }
                                        assignmentsByCenter[appt.center_id].push(appt);
                                    });

                                    return (
                                        <div
                                            key={index}
                                            className={`h-auto bg-white p-2 flex flex-col ${!isCurrentMonthDay ? 'bg-gray-50' : ''
                                                } ${isTodayDate ? 'ring-2 ring-blue-500' : ''}`}
                                        >
                                            <div className={`text-sm font-medium mb-1 ${!isCurrentMonthDay ? 'text-gray-400' :
                                                isTodayDate ? 'text-blue-600' : 'text-gray-900'
                                                }`}>
                                                {date.getDate()}
                                            </div>

                                            <div className="space-y-1 flex-1 overflow-visible">
                                                {Object.entries(assignmentsByCenter).map(([centerId, centerAssignments]) => {
                                                    const sortedAssignments = [...centerAssignments].sort((a, b) => {
                                                        const aStart = a.start || a.startRaw || 0;
                                                        const bStart = b.start || b.startRaw || 0;
                                                        
                                                        if (aStart !== bStart) {
                                                            return aStart - bStart;
                                                        }
                                                        
                                                        const aName = (a.userEmail || a.user_id || '').toLowerCase();
                                                        const bName = (b.userEmail || b.user_id || '').toLowerCase();
                                                        return aName.localeCompare(bName);
                                                    });
                                                    
                                                    return sortedAssignments.map((appt, apptIndex) => {
                                                        const eventColor = getEventColor(appt);
                                                        const colorMap = COLOR_CLASSES;
                                                        const palette = colorMap[eventColor] || colorMap.gray;

                                                        return (
                                                            <div
                                                                key={`${centerId}-${apptIndex}`}
                                                                className={`text-xs p-1 ${palette.border} ${palette.bg} ${palette.text} rounded border-l-4 cursor-pointer hover:shadow-md transition-all`}
                                                                title={`${appt.centerName || centerMap[appt.center_id]} - ${formatTimeStr(appt.startRaw)} - ${formatTimeStr(appt.endRaw)} - ${appt.userEmail || appt.user_id}`}
                                                                onClick={() => handleEventClick(appt)}
                                                            >
                                                                <div className="flex items-center justify-between gap-1">
                                                                    <span className="font-medium text-xs">
                                                                        {(() => {
                                                                            const s = getShiftType(appt.startRaw, appt.endRaw);
                                                                            return s === 'DAY SHIFT' ? 'Day' : s === 'EVENING SHIFT' ? 'Eve' : s === 'NIGHT SHIFT' ? 'Night' : 'Shift';
                                                                        })()}
                                                                    </span>
                                                                    {appt.room_name && (
                                                                        <span className="shrink-0 inline-flex items-center h-[18px] px-1.5 rounded-md bg-white/70 text-slate-700 border border-slate-300 text-[10px] font-semibold leading-none uppercase tracking-wide">
                                                                            {appt.room_name}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs">
                                                                    {(appt.userEmail || appt.user_id || '').split('@')[0]}
                                                                </div>
                                                                {(appt.centerName || centerMap[appt.center_id]) && (
                                                                    <div className="text-xs mt-0.5 opacity-70">
                                                                        <div className="truncate">
                                                                            {appt.centerName || centerMap[appt.center_id]}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {appt.assignment_type === 'draft' && (
                                                                    <div className="mt-1">
                                                                        <span className="px-1 py-0.5 bg-amber-100 text-amber-800 text-xs rounded font-medium">
                                                                            DRAFT
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    });
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {eventDetailsModal && selectedEvent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                                setEventDetailsModal(false);
                                setSelectedEvent(null);
                            }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Assignment Details</h2>
                        <div className="space-y-3">
                            <div>
                                <span className="font-medium text-gray-700">Provider:</span>
                                <span className="ml-2 text-gray-900">{selectedEvent.userEmail || selectedEvent.user_id}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Center:</span>
                                <span className="ml-2 text-gray-900">{selectedEvent.centerName || centerMap[selectedEvent.center_id]}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Date:</span>
                                <span className="ml-2 text-gray-900">{selectedEvent.date}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Time:</span>
                                <span className="ml-2 text-gray-900">{formatTimeStr(selectedEvent.startRaw)} - {formatTimeStr(selectedEvent.endRaw)}</span>
                            </div>
                            {selectedEvent.room_name && (
                                <div>
                                    <span className="font-medium text-gray-700">Room:</span>
                                    <span className="ml-2 text-gray-900">{selectedEvent.room_name}</span>
                                </div>
                            )}
                            {selectedEvent.assignment_type && (
                                <div>
                                    <span className="font-medium text-gray-700">Type:</span>
                                    <span className="ml-2 text-gray-900">
                                        {selectedEvent.assignment_type === 'shift' ? (
                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded font-medium">
                                                {getShiftType(selectedEvent.startRaw, selectedEvent.endRaw)}
                                            </span>
                                        ) : (
                                            selectedEvent.assignment_type
                                        )}
                                    </span>
                                </div>
                            )}
                            {selectedEvent.details && (
                                <div>
                                    <span className="font-medium text-gray-700">Details:</span>
                                    <span className="ml-2 text-gray-900">{selectedEvent.details}</span>
                                </div>
                            )}
                            {selectedEvent.assignment_type === 'draft' && (
                                <div>
                                    <span className="font-medium text-gray-700">Status:</span>
                                    <span className="ml-2">
                                        <span className="px-2 py-1 bg-amber-100 text-amber-800 text-sm rounded font-medium">
                                            Draft — pending review
                                        </span>
                                    </span>
                                </div>
                            )}
                            {selectedEvent.timestamp && (
                                <div>
                                    <span className="font-medium text-gray-700">Created:</span>
                                    <span className="ml-2 text-gray-900">
                                        {selectedEvent.timestamp.slice(0, 10)} {selectedEvent.timestamp.slice(11, 16)}
                                    </span>
                                </div>
                            )}
                            {selectedEvent.request_id && (
                                <div>
                                    <span className="font-medium text-gray-700">Request ID:</span>
                                    <span className="ml-2 text-gray-900 text-sm break-all font-mono">{selectedEvent.request_id}</span>
                                </div>
                            )}
                            {(() => {
                                const req = selectedEvent.request_id
                                    ? pendingRequests.find(r => r.request_id === selectedEvent.request_id)
                                    : null;
                                if (!req) return null;
                                const requestType = (req.request_type || '').replace(/_/g, ' ');
                                const reason = req.reasoning || req.summary || req.user_text || '';
                                const requestedBy = userMap[req.user_id]?.name || userMap[req.user_id]?.email || req.user_id || '';
                                return (
                                    <>
                                        {requestType && (
                                            <div>
                                                <span className="font-medium text-gray-700">Request type:</span>
                                                <span className="ml-2 text-gray-900 capitalize">{requestType}</span>
                                            </div>
                                        )}
                                        {requestedBy && (
                                            <div>
                                                <span className="font-medium text-gray-700">Requested by:</span>
                                                <span className="ml-2 text-gray-900">{requestedBy}</span>
                                            </div>
                                        )}
                                        {reason && (
                                            <div>
                                                <span className="font-medium text-gray-700">Why this draft:</span>
                                                <p className="mt-1 text-gray-900 text-sm whitespace-pre-line bg-amber-50 border border-amber-200 rounded p-2">{reason}</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        {isManager && (
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => handleEditClick(selectedEvent)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-2"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                    Edit Assignment
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isManager && (
                <div className="fixed bottom-6 right-6 z-40 add-menu-container">
                    <button
                        onClick={() => setShowAddMenu(!showAddMenu)}
                        className="w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
                        aria-label="Add"
                    >
                        <PlusIcon className="w-6 h-6" />
                    </button>

                    {showAddMenu && (
                        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[160px]">
                            <button
                                onClick={() => {
                                    setShowAddMenu(false);
                                    openAddModal();
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                                <PlusIcon className="w-4 h-4 mr-2" />
                                Add Assignment
                            </button>
                            <button
                                onClick={() => {
                                    setShowAddMenu(false);
                                    openReportModal();
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                                <DocumentChartBarIcon className="w-4 h-4 mr-2" />
                                Generate Report
                            </button>

                        </div>
                    )}
                </div>
            )}

            {addModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-7xl p-6 rounded shadow relative max-h-[90vh] overflow-y-auto">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                                setAddModalOpen(false);
                                setAddError("");
                                setAddForm({
                                    user_id: "",
                                    center_id: "",
                                    room_name: "",
                                    date: "",
                                    start_time: "",
                                    end_time: "",
                                    details: "",
                                    assignment_type: "assignment",
                                    shift_type: "day",
                                    input_method: "manual",
                                    parse_text: "",
                                });
                                setAddRooms([]);
                                setAddProviderSearch("");
                                setAddCenterSearch("");
                            }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">
                            {addForm.assignment_type === 'shift' ? 'Add Shift' : 'Add Assignment'}
                        </h2>
                        {addError && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                {addError}
                            </div>
                        )}
                        <div className="space-y-4">
                            {true && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Provider</label>
                                            {usersLoading ? (
                                                <div className="text-gray-500 text-sm">Loading users...</div>
                                            ) : (
                                                <>
                                                    <div className="w-full border rounded px-3 py-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500">
                                                        <input
                                                            type="text"
                                                            placeholder="Search providers..."
                                                            value={addProviderSearch}
                                                            onChange={(e) => setAddProviderSearch(e.target.value)}
                                                            className="w-full border-0 outline-none text-sm"
                                                        />
                                                    </div>
                                                    <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto mt-2">
                                                        {(() => {
                                                            const filtered = getFilteredProviders(filteredProviders, addProviderSearch);
                                                            const grouped = groupProvidersByDegree(filtered);
                                                            const degreeOrder = ['MD', 'DO', 'CRNA', 'NP', 'PA', 'RN', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA', 'Other'];
                                                            
                                                            return degreeOrder.filter(degree => grouped[degree]).map(degree => (
                                                                <div key={degree} className="mb-3 last:mb-0">
                                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                                                        {degree}
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        {grouped[degree].map(provider => {
                                                                            const isSelected = addForm.user_id === provider.user_id;
                                                                            const isCertified = certifiedProviders.some(cp => cp.user_id === provider.user_id);
                                                                            const { nameWithoutDegree } = extractDegreeFromName(provider.user_name || provider.email || provider.user_id);
                                                                            return (
                                                                                <div
                                                                                    key={provider.user_id}
                                                                                    className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                                                        isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                                                    }`}
                                                                                    onClick={() => {
                                                                                        setAddForm(prev => ({
                                                                                            ...prev,
                                                                                            user_id: provider.user_id
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        type="radio"
                                                                                        checked={isSelected}
                                                                                        onChange={() => {}}
                                                                                        className="mr-2"
                                                                                    />
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span 
                                                                                            className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                                                            dangerouslySetInnerHTML={{ 
                                                                                                __html: highlightText(nameWithoutDegree, addProviderSearch) 
                                                                                            }}
                                                                                        />
                                                                                        {isCertified && (
                                                                                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 border border-green-200 rounded">
                                                                                                CERTIFIED
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            ));
                                                        })()}
                                                    </div>
                                                    {addForm.user_id && (
                                                        <div className="mt-2">
                                                            <span className="text-sm text-gray-600">Selected: </span>
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                <span className="inline-flex items-center px-2 py-1 text-sm rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                                    {userMap[addForm.user_id]?.name || userMap[addForm.user_id]?.email || addForm.user_id}
                                                                    {certifiedProviders.some(cp => cp.user_id === addForm.user_id) && (
                                                                        <span className="ml-1 px-2 py-1 text-xs bg-green-100 text-green-700 border border-green-200 rounded">
                                                                            CERTIFIED
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Center</label>
                                            {centersLoading ? (
                                                <div className="text-gray-500 text-sm">Loading centers...</div>
                                            ) : (
                                                <>
                                                    <div className="w-full border rounded px-3 py-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500">
                                                        <input
                                                            type="text"
                                                            placeholder="Search centers..."
                                                            value={addCenterSearch}
                                                            onChange={(e) => setAddCenterSearch(e.target.value)}
                                                            className="w-full border-0 outline-none text-sm"
                                                        />
                                                    </div>
                                                    <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto mt-2">
                                                        {getFilteredCenters(centers, addCenterSearch)
                                                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                            .map(center => {
                                                            const isSelected = addForm.center_id === center.center_id;
                                                            const displayName = center.name || center.center_id;
                                                            return (
                                                                <div
                                                                    key={center.center_id}
                                                                    className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                                        isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                                    }`}
                                                                    onClick={() => {
                                                                        setAddForm(prev => ({
                                                                            ...prev,
                                                                            center_id: center.center_id
                                                                        }));
                                                                        setAddForm(prev => ({
                                                                            ...prev,
                                                                            user_id: ""
                                                                        }));
                                                                        setAddProviderSearch("");
                                                                        fetchAddRooms(center.center_id);
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        checked={isSelected}
                                                                        onChange={() => {}}
                                                                        className="mr-2"
                                                                    />
                                                                    <span 
                                                                        className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                                        dangerouslySetInnerHTML={{ 
                                                                            __html: highlightText(displayName, addCenterSearch) 
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {addForm.center_id && (
                                                        <div className="mt-2">
                                                            <span className="text-sm text-gray-600">Selected: </span>
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                <span className="inline-flex items-center px-2 py-1 text-sm rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                                    {centerMap[addForm.center_id] || addForm.center_id}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Room (Optional)</label>
                                            <select
                                                name="room_name"
                                                value={addForm.room_name}
                                                onChange={handleAddFormChange}
                                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                disabled={!addForm.center_id}
                                            >
                                                <option value="">Select a room</option>
                                                {addRooms.map((room, idx) => (
                                                    <option key={idx} value={room}>
                                                        {room}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Type</label>
                                            <select
                                                name="assignment_type"
                                                value={addForm.assignment_type}
                                                onChange={handleAddFormChange}
                                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="assignment">Assignment</option>
                                                <option value="shift">Shift</option>
                                            </select>
                                        </div>
                                    </div>



                                    <div>
                                        <label className="block text-sm font-medium mb-1">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={addForm.date}
                                            onChange={handleAddFormChange}
                                            required
                                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    {addForm.assignment_type === 'assignment' && addForm.input_method === 'manual' ? (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Start Time</label>
                                                <input
                                                    type="time"
                                                    name="start_time"
                                                    value={addForm.start_time}
                                                    onChange={handleAddFormChange}
                                                    required
                                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">End Time</label>
                                                <input
                                                    type="time"
                                                    name="end_time"
                                                    value={addForm.end_time}
                                                    onChange={handleAddFormChange}
                                                    required
                                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Shift Type</label>
                                            <div className="space-y-2">
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="shift_type"
                                                        value="day"
                                                        checked={addForm.shift_type === 'day'}
                                                        onChange={handleAddFormChange}
                                                        className="mr-2"
                                                    />
                                                    <span>Day (7:00 AM - 3:00 PM)</span>
                                                </label>
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="shift_type"
                                                        value="eve"
                                                        checked={addForm.shift_type === 'eve'}
                                                        onChange={handleAddFormChange}
                                                        className="mr-2"
                                                    />
                                                    <span>Evening (3:00 PM - 11:00 PM)</span>
                                                </label>
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="shift_type"
                                                        value="night"
                                                        checked={addForm.shift_type === 'night'}
                                                        onChange={handleAddFormChange}
                                                        className="mr-2"
                                                    />
                                                    <span>Night (11:00 PM - 7:00 AM)</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Details</label>
                                        <input
                                            type="text"
                                            name="details"
                                            value={addForm.details}
                                            onChange={handleAddFormChange}
                                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Optional details"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    onClick={() => {
                                        setAddModalOpen(false);
                                        setAddError("");
                                        setAddForm({
                                            user_id: "",
                                            center_id: "",
                                            room_name: "",
                                            date: "",
                                            start_time: "",
                                            end_time: "",
                                            details: "",
                                            assignment_type: "assignment",
                                            shift_type: "day",
                                            input_method: "manual",
                                            parse_text: "",
                                        });
                                        setAddRooms([]);
                                        setAddProviderSearch("");
                                        setAddCenterSearch("");
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded border-none"
                                    onClick={handleAdd}
                                    disabled={addLoading}
                                >
                                    {addLoading ? "Adding..." : (addForm.assignment_type === 'shift' ? 'Add Shift' : 'Add Assignment')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {editModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-7xl p-6 rounded shadow relative max-h-[90vh] overflow-y-auto">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                                setEditModalOpen(false);
                                setEditingAssignment(null);
                                setEditForm({
                                    user_id: "",
                                    center_id: "",
                                    room_name: "",
                                    date: "",
                                    start_time: "",
                                    end_time: "",
                                    details: "",
                                    assignment_type: "assignment"
                                });
                                setEditRooms([]);
                                setEditError("");
                                setEditProviderSearch("");
                                setEditCenterSearch("");
                            }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Edit Assignment</h2>
                        {editError && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                {editError}
                            </div>
                        )}
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Provider</label>
                                    {usersLoading ? (
                                        <div className="text-gray-500 text-sm">Loading users...</div>
                                    ) : (
                                        <>
                                            <div className="w-full border rounded px-3 py-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500">
                                                <input
                                                    type="text"
                                                    placeholder="Search providers..."
                                                    value={editProviderSearch}
                                                    onChange={(e) => setEditProviderSearch(e.target.value)}
                                                    className="w-full border-0 outline-none text-sm"
                                                />
                                            </div>
                                            <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto mt-2">
                                                {(() => {
                                                    const filtered = getFilteredProviders(filteredProviders, editProviderSearch);
                                                    const grouped = groupProvidersByDegree(filtered);
                                                    const degreeOrder = ['MD', 'DO', 'CRNA', 'NP', 'PA', 'RN', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA', 'Other'];
                                                    
                                                    return degreeOrder.filter(degree => grouped[degree]).map(degree => (
                                                        <div key={degree} className="mb-3 last:mb-0">
                                                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                                                {degree}
                                                            </div>
                                                            <div className="space-y-1">
                                                                {grouped[degree].map(provider => {
                                                                    const isSelected = editForm.user_id === provider.user_id;
                                                                    const isCertified = certifiedProviders.some(cp => cp.user_id === provider.user_id);
                                                                    const { nameWithoutDegree } = extractDegreeFromName(provider.user_name || provider.email || provider.user_id);
                                                                    return (
                                                                        <div
                                                                            key={provider.user_id}
                                                                            className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                                                isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                                            }`}
                                                                            onClick={() => {
                                                                                setEditForm(prev => ({
                                                                                    ...prev,
                                                                                    user_id: provider.user_id
                                                                                }));
                                                                            }}
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                checked={isSelected}
                                                                                onChange={() => {}}
                                                                                className="mr-2"
                                                                            />
                                                                            <div className="flex items-center gap-2">
                                                                                <span 
                                                                                    className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                                                    dangerouslySetInnerHTML={{ 
                                                                                        __html: highlightText(nameWithoutDegree, editProviderSearch) 
                                                                                    }}
                                                                                />
                                                                                {isCertified && (
                                                                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 border border-green-200 rounded">
                                                                                        CERTIFIED
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                            {editForm.user_id && (
                                                <div className="mt-2">
                                                    <span className="text-sm text-gray-600">Selected: </span>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <span className="inline-flex items-center px-2 py-1 text-sm rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                            {userMap[editForm.user_id]?.name || userMap[editForm.user_id]?.email || editForm.user_id}
                                                            {editingAssignment && editingAssignment.isDraft ? (
                                                                <span className="ml-2 text-xs text-gray-600">({editForm.user_id})</span>
                                                            ) : null}
                                                            {certifiedProviders.some(cp => cp.user_id === editForm.user_id) && (
                                                                <span className="ml-1 px-2 py-1 text-xs bg-green-100 text-green-700 border border-green-200 rounded">
                                                                    CERTIFIED
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Center</label>
                                    {centersLoading ? (
                                        <div className="text-gray-500 text-sm">Loading centers...</div>
                                    ) : (
                                        <>
                                            {editingAssignment && editingAssignment.isDraft ? (
                                                <div className="mt-2">
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <span className="inline-flex items-center px-2 py-1 text-sm rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                            {centerMap[editForm.center_id] || editForm.center_id}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">Center is fixed for this draft.</div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-full border rounded px-3 py-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500">
                                                        <input
                                                            type="text"
                                                            placeholder="Search centers..."
                                                            value={editCenterSearch}
                                                            onChange={(e) => setEditCenterSearch(e.target.value)}
                                                            className="w-full border-0 outline-none text-sm"
                                                        />
                                                    </div>
                                                    <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto mt-2">
                                                        {getFilteredCenters(centers, editCenterSearch)
                                                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                            .map(center => {
                                                            const isSelected = editForm.center_id === center.center_id;
                                                            const displayName = center.name || center.center_id;
                                                            return (
                                                                <div
                                                                    key={center.center_id}
                                                                    className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                                        isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                                    }`}
                                                                    onClick={() => {
                                                                        setEditForm(prev => ({
                                                                            ...prev,
                                                                            center_id: center.center_id
                                                                        }));
                                                                        setEditForm(prev => ({
                                                                            ...prev,
                                                                            user_id: ""
                                                                        }));
                                                                        setEditProviderSearch("");
                                                                        fetchEditRooms(center.center_id);
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        checked={isSelected}
                                                                        onChange={() => {}}
                                                                        className="mr-2"
                                                                    />
                                                                    <span 
                                                                        className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                                        dangerouslySetInnerHTML={{ 
                                                                            __html: highlightText(displayName, editCenterSearch) 
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}
                                            {editForm.center_id && (
                                                <div className="mt-2">
                                                    <span className="text-sm text-gray-600">Selected: </span>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <span className="inline-flex items-center px-2 py-1 text-sm rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                            {centerMap[editForm.center_id] || editForm.center_id}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Room (Optional)</label>
                                    <select
                                        name="room_name"
                                        value={editForm.room_name}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={!editForm.center_id}
                                    >
                                        <option value="">Select a room</option>
                                        {editRooms.map((room, idx) => (
                                            <option key={idx} value={room}>
                                                {room}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Type</label>
                                    <select
                                        name="assignment_type"
                                        value={editForm.assignment_type || "assignment"}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="assignment">Assignment</option>
                                        <option value="shift">Shift</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Date</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={editForm.date}
                                    onChange={handleEditFormChange}
                                    required
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Start Time</label>
                                    <input
                                        type="time"
                                        name="start_time"
                                        value={editForm.start_time}
                                        onChange={handleEditFormChange}
                                        required
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">End Time</label>
                                    <input
                                        type="time"
                                        name="end_time"
                                        value={editForm.end_time}
                                        onChange={handleEditFormChange}
                                        required
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Details</label>
                                <input
                                    type="text"
                                    name="details"
                                    value={editForm.details}
                                    onChange={handleEditFormChange}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Optional details"
                                />
                            </div>
                            <div className="flex justify-between mt-6">
                                <button
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-2"
                                    onClick={handleDelete}
                                    disabled={editLoading}
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    {editLoading ? "Deleting..." : "Delete"}
                                </button>
                                <div className="flex gap-2">
                                    <button
                                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded"
                                        onClick={() => {
                                            setEditModalOpen(false);
                                            setEditingAssignment(null);
                                            setEditForm({
                                                user_id: "",
                                                center_id: "",
                                                room_name: "",
                                                date: "",
                                                start_time: "",
                                                end_time: "",
                                                details: ""
                                            });
                                            setEditRooms([]);
                                            setEditError("");
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                                        onClick={handleEdit}
                                        disabled={editLoading}
                                    >
                                        {editLoading ? "Updating..." : "Update Assignment"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {addCenterModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-2xl p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                                setAddCenterModalOpen(false);
                                setCenterError("");
                                setCenterForm({
                                    name: "",
                                    location: "",
                                    rooms: [],
                                    roomName: "",
                                });
                            }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Add Facility</h2>
                        {centerError && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                {centerError}
                            </div>
                        )}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Facility Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={centerForm.name}
                                    onChange={handleCenterFormChange}
                                    required
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter facility name"
                                />
                                {!centerForm.name.trim() && (
                                    <p className="mt-1 text-sm text-red-600">Facility name is required</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Location</label>
                                <input
                                    type="text"
                                    name="location"
                                    value={centerForm.location}
                                    onChange={handleCenterFormChange}
                                    required
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter center location"
                                />
                                {!centerForm.location.trim() && (
                                    <p className="mt-1 text-sm text-red-600">Location is required</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Facility Type</label>
                                <select
                                    name="type"
                                    value={centerForm.type}
                                    onChange={handleCenterFormChange}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="medical_center">Medical Center</option>
                                    <option value="hospital">Hospital</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Rooms</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        name="roomName"
                                        value={centerForm.roomName}
                                        onChange={handleCenterFormChange}
                                        className="flex-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Room name"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddCenterRoom}
                                        className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {centerForm.rooms.map((room, idx) => (
                                        <span key={idx} className="inline-flex items-center px-2 py-1 text-xs rounded border bg-gray-100 text-gray-500 border-gray-200">
                                            {room}
                                            <button
                                                type="button"
                                                className="ml-1 text-red-500 hover:text-red-700"
                                                onClick={() => handleRemoveCenterRoom(idx)}
                                                title="Remove room"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    style={{ minWidth: '80px', height: 'auto' }}
                                    onClick={() => {
                                        setAddCenterModalOpen(false);
                                        setCenterError("");
                                        setCenterForm({
                                            name: "",
                                            location: "",
                                            rooms: [],
                                            roomName: "",
                                        });
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded border-none"
                                    style={{ minWidth: '80px', height: 'auto' }}
                                    onClick={handleAddCenter}
                                    disabled={centerLoading}
                                >
                                    {centerLoading ? "Adding..." : "Add Center"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {reportModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-7xl p-6 rounded shadow relative max-h-[90vh] overflow-y-auto">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                                setReportModalOpen(false);
                                setReportError("");
                                setReportData(null);
                                setCenterSearch("");
                                setProviderSearch("");
                                setSelectedReportCenters(new Set());
                                setSelectedReportProviders(new Set());

                                setReportForm({
                                    reportType: 'all_shifts',
                                    startDate: '',
                                    endDate: '',
                                    centerIds: [],
                                    userIds: []
                                });
                            }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>

                        <h2 className="text-xl font-bold mb-4">Generate Provider Report</h2>
                        <p className="text-sm text-gray-600 mb-6">
                            Generate billing reports for providers. All providers are selected by default. Time range is required.
                        </p>

                        {reportError && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                {reportError}
                            </div>
                        )}

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={reportForm.startDate}
                                        onChange={handleReportFormChange}
                                        required
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">End Date</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={reportForm.endDate}
                                        onChange={handleReportFormChange}
                                        required
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>



                            <div>
                                <label className="block text-sm font-medium mb-2">Providers (All selected by default)</label>
                                <div className="w-full border rounded px-3 py-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500">
                                    <input
                                        type="text"
                                        placeholder="Search providers..."
                                        value={providerSearch}
                                        onChange={(e) => setProviderSearch(e.target.value)}
                                        className="w-full border-0 outline-none text-sm"
                                    />
                                </div>
                                <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto mt-2">
                                    {getFilteredProviders(users, providerSearch)
                                        .sort((a, b) => (a.user_name || a.email || '').localeCompare(b.user_name || b.email || ''))
                                        .map(provider => {
                                        const isSelected = selectedReportProviders.has(provider.user_id);
                                        const displayName = provider.user_name || provider.email || provider.user_id;
                                        return (
                                            <div
                                                key={provider.user_id}
                                                className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                    isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                }`}
                                                onClick={() => {
                                                    setSelectedReportProviders(prev => {
                                                        const newSet = new Set(prev);
                                                        if (newSet.has(provider.user_id)) {
                                                            newSet.delete(provider.user_id);
                                                        } else {
                                                            newSet.add(provider.user_id);
                                                        }
                                                        return newSet;
                                                    });
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    className="mr-2"
                                                />
                                                <span 
                                                    className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                    dangerouslySetInnerHTML={{ 
                                                        __html: highlightText(displayName, providerSearch) 
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {selectedReportProviders.size > 0 && (
                                    <div className="mt-2">
                                        <span className="text-sm text-gray-600">Selected: </span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {Array.from(selectedReportProviders).map(userId => (
                                                <span key={userId} className="inline-flex items-center px-2 py-1 text-sm rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                    {userMap[userId]?.name || userMap[userId]?.email || userId}
                                                    <button
                                                        type="button"
                                                        className="ml-1 text-blue-500 hover:text-blue-700"
                                                        onClick={() => setSelectedReportProviders(prev => {
                                                            const newSet = new Set(prev);
                                                            newSet.delete(userId);
                                                            return newSet;
                                                        })}
                                                        title="Remove provider"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>



                            <div className="flex justify-end gap-2">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded"
                                    onClick={() => {
                                        setReportModalOpen(false);
                                        setReportError("");
                                        setReportData(null);
                                        setCenterSearch("");
                                        setProviderSearch("");
                                        setSelectedReportCenters(new Set());
                                        setSelectedReportProviders(new Set());
                                        setReportForm({
                                            startDate: '',
                                            endDate: ''
                                        });
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                                    onClick={generateReport}
                                    disabled={reportLoading || !canGenerateReport()}
                                >
                                    {reportLoading ? "Generating..." : "Generate Report"}
                                </button>
                            </div>
                        </div>

                        {reportData && (
                            <div className="mt-8 border-t pt-6">
                                <h3 className="text-lg font-semibold mb-4">Provider Report Results</h3>
                                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
                                    Successfully generated report with {reportData.totalShifts} shifts found across {Object.keys(reportData.groupedShifts).length} providers.
                                </div>
                                
                                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <span className="font-medium">Date Range:</span> {reportData.startDate} to {reportData.endDate}
                                        </div>
                                        <div>
                                            <span className="font-medium">Providers:</span> {reportData.userIds.map(id => userMap[id]?.name || id).join(', ')}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-6">
                                    {Object.keys(reportData.groupedShifts).map(providerId => {
                                        const providerName = userMap[providerId]?.name || providerId;
                                        const shifts = reportData.groupedShifts[providerId];
                                        
                                        return (
                                            <div key={providerId} className="border border-gray-200 rounded-lg">
                                                <div className="bg-gray-50 px-4 py-3 border-b">
                                                    <h4 className="text-lg font-semibold text-gray-800">
                                                        {providerName} ({shifts.length} shifts)
                                                    </h4>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Date</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Center</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Room</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Time</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Shift Type</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {shifts.map((shift, index) => (
                                                                <tr key={index} className="hover:bg-gray-50">
                                                                    <td className="px-3 py-2 text-sm text-gray-900 border-b">
                                                                        {shift.date}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-gray-900 border-b">
                                                                        {centerMap[shift.center_id] || shift.center_id}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-gray-900 border-b">
                                                                        {shift.room_name || '-'}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-gray-900 border-b">
                                                                        {formatTimeStr(shift.start_time)} - {formatTimeStr(shift.end_time)}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-sm text-gray-900 border-b">
                                                                        {getShiftType(shift.start_time, shift.end_time)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                
                                <div className="mt-4 text-center">
                                    <button
                                        onClick={downloadCSV}
                                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded flex items-center gap-2 mx-auto"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        Download CSV
                                    </button>
                                </div>
                                
                                <div className="mt-4 text-center">
                                    <p className="text-sm text-gray-600">
                                        Showing {reportData.shifts.length} shifts across {Object.keys(reportData.groupedShifts).length} providers
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {datePickerModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => setDatePickerModalOpen(false)}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Go to Date
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Date
                                </label>
                                <input
                                    type="date"
                                    value={selectedDateForPicker.toISOString().split('T')[0]}
                                    onChange={(e) => setSelectedDateForPicker(new Date(e.target.value))}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setDatePickerModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    goToDate(selectedDateForPicker, isDailyView, isMonthlyView, setDailyViewDate, setMonthlyViewDate, setSelectedDate);
                                    setDatePickerModalOpen(false);
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                            >
                                Go to Date
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default Calendar; 